import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar } from 'lucide-react';
import PollPage from './components/PollPage';
import PollResultsPage from './components/PollResultsPage';
import ChooseCalendarStep from './components/ChooseCalendarStep';
import MemberCalendarStep from './components/MemberCalendarStep';
import CreateCalendarStep from './components/CreateCalendarStep';
import WelcomeStep from './components/WelcomeStep';
import OnboardingStep from './components/OnboardingStep';
import DashboardStep from './components/DashboardStep';
import HelpPage from './components/HelpPage';
import { missingIndexMessage, withoutBareKeyword } from './utils/calendarHistory'; // (9 Oct 2026, 8.19/8.20, decisions 64-67)
// (22 Sep 2026) PreferredNameGate retired — replaced by SettingsModal
// itself, opened straight to Profile with requireProfileCompletion, so a
// new/incomplete account fills in the same fields other members use
// (Settings > Profile) rather than a separate bespoke screen. The old
// component still exists at ./components/PreferredNameGate.jsx but
// nothing imports it now — safe to delete from the repo.
import SettingsModal from './components/SettingsModal';
import StatusToast from './components/StatusToast';
import {
  CURRENCY_SYMBOLS,
  CURRENCY_NAMES,
  formatPrice,
  getNextQuarterHour,
  getEndTime,
  formatTime,
  formatShortDate,
  formatDateByPref,
  formatEventDateByPref,
  getTzAbbr
} from './utils/formatting';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://whatsapp-calendar-backend-production.up.railway.app';

// (21 Oct 2026, 8.42, decisions 107, 113, 115) A date in full for WhatsApp messages,
// e.g. "Sunday 25 October", read from YYYY-MM-DD as it is (no time zone can shift the day).
// The same words as emailService.js and pollScheduler.js.
const fullDayDate = (isoDate) => {
  const iso = String(isoDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
};

// (16 Sep 2026) Was declared identically inline in both addEvent and
// saveEditEvent below — hoisted here so there's one definition instead of
// two to keep in sync. Drives the Tier Error Modal in DashboardStep.jsx.
const TIER_LIMIT_ERROR_CODES = ['TIER_EXPIRED', 'OUTSIDE_PERIOD', 'SPACING_VIOLATION', 'EVENT_LIMIT'];

if (!window.storage) {
  window.storage = {
    async get(key) {
      const value = localStorage.getItem(key);
      return value ? { key, value } : null;
    },
    async set(key, value) {
      localStorage.setItem(key, value);
      return { key, value };
    },
    async delete(key) {
      localStorage.removeItem(key);
      return { key, deleted: true };
    },
    async list(prefix) {
      const keys = Object.keys(localStorage).filter(k => !prefix || k.startsWith(prefix));
      return { keys };
    }
  };
}

// ============================================
// SESSION GUARD (1 Oct 2026, B1)
// ============================================
// A member must never be left on a dashboard that is no longer signed in
// (seen 1 Oct on the desktop: Settings > Members showed "Invalid token" and
// the Sign Out button had vanished, because the stored sign-in had been
// removed while the screen stayed up). Four things now end a session
// cleanly — forget the sign-in, reload to the sign-in screen and say why:
//   1. The server refuses the sign-in (middleware.js answers 403 "Invalid
//      token" — expired after 7 days, or no longer accepted). Every API call
//      goes through window.fetch, so one check here covers App.js and every
//      component (SettingsModal, PollPage, ...).
//   2. The sign-in's own expiry time has passed (checked on load and every
//      30 seconds by the idle check).
//   3. Another tab or window of the same browser signed out, timed out or
//      signed in with a different email (all tabs share one stored sign-in).
//   4. The 20-minute idle timeout (unchanged rule; activity in ANY tab now
//      counts, so an idle background tab no longer signs out a tab in use).
const AUTH_TOKEN_KEY = 'auth_token';
const SIGNIN_NOTICE_KEY = 'signin_notice';      // sessionStorage: why this tab was signed out
const LAST_ACTIVITY_KEY = 'last_activity_at';   // localStorage: shared by all tabs
const SESSION_IDLE_MS = 20 * 60 * 1000;         // 20 minutes
const SIGNIN_NOTICES = {
  expired: 'Your sign-in has expired. Please sign in again.',
  idle: 'You were signed out after 20 minutes without activity. Please sign in again.',
  elsewhere: 'You were signed out in another tab or window. Please sign in again.',
  switched: 'You signed in with a different email in another tab, so this tab now shows that account.',
  // (11 Oct 2026, 8.4) Not a sign-out: this "Email sent!" tab took over the sign-in.
  handover: 'Signed in successfully!',
};

const readTokenPayload = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch (_) {
    return null;
  }
};

const tokenHasExpired = (token) => {
  const payload = token ? readTokenPayload(token) : null;
  return !!(payload && typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now());
};

const sharedLastActivity = () => {
  try { return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0; } catch (_) { return 0; }
};

// Was this tab signed in when it loaded (or arriving with a fresh sign-in)?
// A tab sitting on the sign-in screen ignores other tabs signing out.
const tabStartedSignedIn = (() => {
  try {
    return !!localStorage.getItem(AUTH_TOKEN_KEY) ||
      !!new URLSearchParams(window.location.search).get('token');
  } catch (_) {
    return false;
  }
})();

let sessionEnding = false;
const endSession = (reason, { keepToken = false } = {}) => {
  if (sessionEnding) return;
  sessionEnding = true;
  if (!keepToken) { try { localStorage.removeItem(AUTH_TOKEN_KEY); } catch (_) {} }
  try { sessionStorage.setItem(SIGNIN_NOTICE_KEY, reason); } catch (_) {}
  window.location.reload();
};

// (2) On load: a stored sign-in that has already run out is forgotten
// before anything is drawn, so the app opens on the sign-in screen with
// the reason, instead of a half-loaded screen.
try {
  const storedAtLoad = localStorage.getItem(AUTH_TOKEN_KEY);
  if (storedAtLoad && !new URLSearchParams(window.location.search).get('token') && tokenHasExpired(storedAtLoad)) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    sessionStorage.setItem(SIGNIN_NOTICE_KEY, 'expired');
  }
} catch (_) {}

// (1) Server refused the sign-in. Acts only when the refused request
// carried this browser's current sign-in, so an old tab holding an older
// pass can never sign out a newer one.
const sentBearerToken = (input, init) => {
  let headers = init && init.headers;
  if (!headers && input && typeof input !== 'string' && input.headers) headers = input.headers;
  if (!headers) return null;
  const value = typeof headers.get === 'function'
    ? headers.get('Authorization')
    : (headers.Authorization || headers.authorization);
  const match = /^Bearer\s+(\S+)$/.exec(value || '');
  return match && match[1].split('.').length === 3 ? match[1] : null;
};

if (!window.__calendarHubSessionGuard) {
  window.__calendarHubSessionGuard = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    try {
      if (response.status === 403) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const sent = url.startsWith(API_BASE_URL) ? sentBearerToken(input, init) : null;
        if (sent) {
          const body = await response.clone().json().catch(() => null);
          const stored = localStorage.getItem(AUTH_TOKEN_KEY);
          if (body && body.error === 'Invalid token' && (!stored || stored === sent)) {
            endSession('expired');
          }
        }
      }
    } catch (_) {}
    return response;
  };

  // (3) Another tab changed the shared sign-in.
  window.addEventListener('storage', (e) => {
    if (e.key !== AUTH_TOKEN_KEY || !tabStartedSignedIn) return;
    if (e.oldValue && !e.newValue) {
      const idle = Date.now() - sharedLastActivity() >= SESSION_IDLE_MS;
      endSession(idle ? 'idle' : 'elsewhere');
    } else if (e.oldValue && e.newValue) {
      const before = (readTokenPayload(e.oldValue) || {}).email;
      const after = (readTokenPayload(e.newValue) || {}).email;
      if (before && after && before.toLowerCase() !== after.toLowerCase()) {
        endSession('switched', { keepToken: true });
      }
    }
  });
}

// ============================================
// SIGN-IN HAND-OVER TO THE WAITING TAB (11 Oct 2026, 8.4, decision 37)
// ============================================
// Clicking the sign-in link opens a NEW tab, while the tab that asked for
// the email is left on "Email sent!" — and repeated sign-ins piled up such
// tabs. Now the two tabs talk (BroadcastChannel; same browser only):
//   1. The link's tab, once signed in, asks "anyone waiting for {email}?".
//   2. Each "Email sent!" tab for that email answers. The most recent one
//      is chosen; any others read "You're signed in in another tab. You can
//      close this one."
//   3. The link's tab tries to close itself. If the browser allows it, the
//      chosen tab is told as the link's tab goes, and reloads signed in. If
//      the browser refuses (most do for a tab the person opened), the link's
//      tab stays signed in and the chosen tab reads the same message as above.
// Browsers without BroadcastChannel simply keep the old behaviour.
const SIGNIN_CHANNEL = 'calendarhub-signin';
const SIGNIN_ASK_WAIT_MS = 600;     // link's tab: time to collect answers
const SIGNIN_CLOSE_WAIT_MS = 500;   // link's tab: still here after this = close refused
const SIGNIN_TAKEOVER_WAIT_MS = 3000; // waiting tab: no word after this = take over anyway

const newTabId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const openSigninChannel = () => {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SIGNIN_CHANNEL);
  } catch (_) {
    return null;
  }
};

// The link's tab: called once, right after the fresh sign-in is stored.
const handOverSignIn = (email) => {
  const channel = openSigninChannel();
  if (!channel || !email) return;
  const myId = newTabId();
  const answers = [];
  channel.onmessage = (e) => {
    const m = e.data || {};
    if (m.type === 'waiting' && m.to === myId && m.from) answers.push(m);
  };
  channel.postMessage({ type: 'signed-in', from: myId, email: String(email).toLowerCase() });

  setTimeout(() => {
    if (answers.length === 0) { channel.close(); return; }
    // The most recent "Email sent!" tab takes over; the others are told to stay put.
    answers.sort((a, b) => (b.sentAt || 0) - (a.sentAt || 0));
    const chosen = answers[0];
    answers.slice(1).forEach((a) => channel.postMessage({ type: 'stay-put', to: a.from }));
    const onGoing = () => {
      try { channel.postMessage({ type: 'take-over', to: chosen.from }); } catch (_) {}
    };
    window.addEventListener('pagehide', onGoing);
    try { window.close(); } catch (_) {}
    // Still running, so the browser refused to close this tab: it stays signed in.
    setTimeout(() => {
      window.removeEventListener('pagehide', onGoing);
      channel.postMessage({ type: 'stay-put', to: chosen.from });
      channel.close();
    }, SIGNIN_CLOSE_WAIT_MS);
  }, SIGNIN_ASK_WAIT_MS);
};



// ============================================
// MAIN APP COMPONENT
// ============================================

const GoogleCalendarApp = () => {

 // ============================================
// AUTHENTICATION HELPER FUNCTIONS
// ============================================

const getAuthToken = () => localStorage.getItem('auth_token');

const setAuthToken = (token) => localStorage.setItem('auth_token', token);

const clearAuthToken = () => localStorage.removeItem('auth_token');

const isAuthenticated = () => !!getAuthToken();

// Decode the email out of a JWT without verifying it (verification happens
// server-side on every authenticated request) — used both right after a
// fresh magic-link redirect and as a fallback on later visits/reloads,
// where the URL's one-time token param is long gone but the stored token
// is still valid.
const decodeEmailFromToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    return payload.email || null;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

const authenticatedFetch = async (url, options = {}) => {
  const token = getAuthToken();
  if (!token) throw new Error('Not authenticated');

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers
  };

  const response = await fetch(url, { ...options, headers });

  // Only treat as auth error if it's actually an auth issue
  // 403 with error details = tier limit, not auth problem
  if (response.status === 401) {
    // (1 Oct 2026, B1) Was clearAuthToken() alone, which left the screen up
    // with no sign-in behind it (and no Sign Out button). Now ends the
    // session properly: sign-in screen plus the reason.
    endSession('expired');
    throw new Error('Authentication expired. Please sign in again.');
  }

  return response;
};

  // (8 Oct 2026, decision 33) One plain message whenever the sign-in email can't be
  // sent, including when Calendar Hub itself can't be reached.
  const SIGN_IN_SEND_FAILED = "We couldn't send your sign-in email just now. Please try again in a minute.";
  const requestMagicLink = async (email) => {
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/auth/request-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
    } catch (_) {
      throw new Error(SIGN_IN_SEND_FAILED);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || SIGN_IN_SEND_FAILED);
    }

    const data = await response.json();

    // Beta access restriction — surface the message as an error so it displays in the UI
    if (data.betaRestricted) {
      throw new Error(data.message);
    }

    return data;
  };

  const [step, setStep] = useState('welcome');
  // (21 Sep 2026) A phone browser keeps the scroll position when one screen is swapped for
  // another, so a poll screen opened from a scrolled list came up already scrolled, with its
  // first card tucked under the sticky header. Start the poll screens at the top.
  useEffect(() => {
    if (step === 'poll-page' || step === 'poll-results') window.scrollTo(0, 0);
  }, [step]);
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [calendars, setCalendars] = useState([]);
  const [currentCalendar, setCurrentCalendar] = useState(null);
  const [events, setEvents] = useState([]);
  // (11 Oct 2026, 8.41) Counts changes to the events list made here (add, edit,
  // delete, calendar switch). A quiet reload (reload on return, 8.1) that began
  // before such a change is ignored when it answers after it: its list is older
  // than what is on screen (seen 11 Oct: a deleted event came back until the next
  // reload).
  const eventsSeqRef = useRef(0);
  const setEventsChanged = (list) => {
    eventsSeqRef.current += 1;
    setEvents(list);
  };
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [calendarName, setCalendarName] = useState('');
  const [calendarDescription, setCalendarDescription] = useState('');
  // (20 Sep 2026) Create-calendar form: does the new calendar use a host schedule board?
  const [newCalendarHostBoard, setNewCalendarHostBoard] = useState(false);
  // (20 Sep 2026) Host board: the board month an Add Event form was opened from, so
  // saving the event links the month to it; and a counter that makes the board reload
  // whenever events change or a board announcement is sent.
  const [pendingHostSlotId, setPendingHostSlotId] = useState(null);
  const [hostBoardVersion, setHostBoardVersion] = useState(0);
  // (20 Sep 2026) Host board tasks (reconfirmation requests and notes) shown in the Notifications bell
  const [hostBoardTasks, setHostBoardTasks] = useState([]);
  const [resolvingBoardTaskId, setResolvingBoardTaskId] = useState(null);
  useEffect(() => {
    if (!showAddEvent) setPendingHostSlotId(null);
  }, [showAddEvent]);
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    time: getNextQuarterHour(),
    endTime: getEndTime(getNextQuarterHour()),
    location: '',
    description: ''
  });
  const [loginEmail, setLoginEmail] = useState('');
  const [savedEmails, setSavedEmails] = useState([]);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  // (11 Oct 2026, 8.4) When this tab sent its sign-in email, and whether another
  // tab of this browser has since signed in instead (WelcomeStep says so).
  const magicLinkSentAtRef = useRef(0);
  const [signedInElsewhere, setSignedInElsewhere] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' }); // type: 'success', 'error', 'info'
  
  // (11 Sep 2026) Wrapped in useCallback — previously a plain function
  // recreated on every App.js render. Any consumer that (correctly) lists
  // onStatusMessage in a useEffect dependency array — e.g.
  // PollSettingsTab's data-load effect — would then see a NEW reference
  // on every statusMessage change (both the initial toast and its own
  // delayed clear ~duration ms later), re-firing that effect and
  // producing a visible reload/flicker each time. Empty deps: this
  // function only calls setStatusMessage with its own arguments, no
  // outside state.
  // (21 Sep 2026) statusTimerRef: each toast now cancels the previous toast's clear-timer, so a
  // long-lived toast (e.g. the Run Scheduler result) can no longer wipe a newer one early.
  // A toast that stays 8 seconds or more is flagged dismissible and gets an OK button.
  const statusTimerRef = useRef(null);
  // (26 Sep 2026) How long messages stay, in seconds — the signed-in user's
  // own setting (users.message_seconds / error_message_seconds, set by an
  // administrator in Settings > Preferences), else 4 and 8. A ref, so
  // showStatus can keep its empty dependency list (see above).
  const messageTimesRef = useRef({ normal: 4, error: 8 });
  const clearStatus = useCallback(() => {
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    setStatusMessage({ text: '', type: '' });
  }, []);
  const showStatus = useCallback((text, type = 'info', durationArg) => {
    // (26 Sep 2026) Every message now follows the setting: errors use the
    // error time; everything else the ordinary time — except a caller that
    // asked for a long message (8 s or more, or 'long'), which gets the error
    // time too (e.g. "added, but the email could not be sent"). Long
    // messages get a × to close them early; each shows when it will go.
    const isLong = type === 'error' || durationArg === 'long' || (typeof durationArg === 'number' && durationArg >= 8000);
    const seconds = isLong ? messageTimesRef.current.error : messageTimesRef.current.normal;
    const duration = seconds * 1000;
    // Force a delay to ensure the toast is visible
    setStatusMessage({
      text,
      type,
      seconds: text ? seconds : null,
      dismissible: text ? isLong : false,
      dismiss: clearStatus,
    });
    
    // (25 Oct 2026, decision 137) The timer starts at once. It used to start inside
    // requestAnimationFrame, which Safari pauses while the page is in the background:
    // a message shown as WhatsApp opened (the bell's "Send via WhatsApp") stayed on
    // screen indefinitely on Neil's iPhone. A timer that falls due while the page is
    // hidden runs as soon as the page is back.
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    statusTimerRef.current = setTimeout(() => {
      setStatusMessage({ text: '', type: '' });
    }, duration);
  }, [clearStatus]);

  // (24 Oct 2026, 8.40a, decision 100) Settings > Preferences announces the Date Format and
  // the day-of-the-week choice when saved, so event dates change at once (the Date
  // Format used to apply only after a reload).
  useEffect(() => {
    const onDatePrefs = (e) => {
      const d = e.detail || {};
      if (d.dateFormat) setUserTier((prev) => ({ ...(prev || {}), preferred_date_format: d.dateFormat }));
      if (typeof d.showWeekday === 'boolean') setShowWeekday(d.showWeekday);
    };
    window.addEventListener('calendarhub:date-prefs', onDatePrefs);
    return () => window.removeEventListener('calendarhub:date-prefs', onDatePrefs);
  }, []);

  // (26 Sep 2026) Settings > Preferences announces new message times when saved.
  useEffect(() => {
    const onTimes = (e) => {
      const d = e.detail || {};
      if (Number(d.normal) > 0) messageTimesRef.current.normal = Number(d.normal);
      if (Number(d.error) > 0) messageTimesRef.current.error = Number(d.error);
    };
    window.addEventListener('calendarhub:message-times', onTimes);
    return () => window.removeEventListener('calendarhub:message-times', onTimes);
  }, []);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRenameCalendar, setShowRenameCalendar] = useState(false);
  const [renameCalendarInput, setRenameCalendarInput] = useState('');
  const [tierError, setTierError] = useState(null); // { code, message }
  // WhatsApp notification prompt — set after any successful calendar/event action
  // shape: { type: 'event_add'|'event_edit'|'event_delete'|'calendar_rename',
  //          subject: string, changes: string|null, calendarName: string,
  //          shortCode: string|null, eventId: string|null, calendarId: string|null }
  const [whatsappNotifyModal, setWhatsappNotifyModal] = useState(null);
  // (6 Oct 2026, H18 Stage 2) Prompts waiting their turn: "instead of it" deletes the
  // old event and adds the new one in one go, so its two prompts (deleted, then added)
  // are shown one after the other.
  const pendingNotifyRef = useRef([]);
  useEffect(() => {
    if (!whatsappNotifyModal && pendingNotifyRef.current.length > 0) {
      setWhatsappNotifyModal(pendingNotifyRef.current.shift());
    }
  }, [whatsappNotifyModal]);
  // (6 Oct 2026, H18 Stage 2) The hosting-schedule question raised by Add Event, or by
  // "Add it to the hosting schedule only for now": { source: 'event'|'scheduleOnly',
  // situation, monthLabel, slotId, existingEvent, existingDay }. null = no question.
  const [scheduleChoice, setScheduleChoice] = useState(null);
  // (11 Oct 2026, Stage 3) Questions raised by Edit Event when an event's date changes:
  // { type: 'history', name, oldDate, newDate } or { type: 'poll', timetable }. The
  // answers given so far wait in editAnswersRef and are sent again with the next try.
  const [editQuestion, setEditQuestion] = useState(null);
  const editAnswersRef = useRef({});
  // (24 Oct 2026, decision 131) The Poll dates window for an event that is not moving,
  // opened from its card: { eventUid, title, mode: 'stay' | 'resume', timetable }.
  const [pollDatesQuestion, setPollDatesQuestion] = useState(null);
  const [showDeleteEventConfirm, setShowDeleteEventConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEditEvent, setShowEditEvent] = useState(false);
  const editEventRef = useRef(null);
  // Calendar-level timezone (loaded with calendar data, saved to DB via PATCH)
  const [calendarTimezone, setCalendarTimezone] = useState('Europe/London');
  // Per-event timezone UI state (Step 5)
  const [eventUseCustomTz, setEventUseCustomTz] = useState(false);
  const [eventDiffEndTz, setEventDiffEndTz] = useState(false);
  const [eventStartTz, setEventStartTz] = useState('');
  const [eventEndTz, setEventEndTz] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState('profile');
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [selectedPaidTier, setSelectedPaidTier] = useState(null);
  const [onboardingTiersList, setOnboardingTiersList] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [activatingTier, setActivatingTier] = useState(false);
  const [activatingTierName, setActivatingTierName] = useState(null);
  const [expandedTierGroup, setExpandedTierGroup] = useState(null);
  const [onboardingLang, setOnboardingLang] = useState('en');
  const [onboardingCurrency, setOnboardingCurrency] = useState('GBP');
  const [currencyManuallySet, setCurrencyManuallySet] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [isSA, setIsSA] = useState(false);
  const [raRegions, setRaRegions] = useState([]);
  // (12 Sep 2026) landing-context already returns authorityCalendars
  // (per-calendar role + isActiveHolder) alongside isSA/raRegions — only
  // the latter two were ever captured into state. Needed so the Profile
  // tab can show a complete "your roles" summary without a second fetch.
  const [authorityCalendars, setAuthorityCalendars] = useState([]);
  // (22 Sep 2026) Same landing-context value used this morning to fix the
  // create-calendar landing-page routing (App.js's needs-profile-completion
  // block, and landingService.js) — stored here too now, so DashboardStep's
  // "+ New Calendar" button can check it as well. Confirmed live: that
  // button's disabled condition only ever checked the tier calendar-count
  // limit, never whether the account holds the region-scoped 'ga' row
  // calendarRoutes.js's create route actually requires — so a stand-in
  // (zero owned calendars, so never at the count limit) saw an enabled
  // button that would 403 on submit, same failure mode as this morning's
  // routing bug, just in a different screen.
  const [canCreateCalendar, setCanCreateCalendar] = useState(false);
  // (7 Oct 2026, 8.28) A secondary contact on the first-sign-in screen: First and Last Name only.
  const [isSecondaryOnly, setIsSecondaryOnly] = useState(false);
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [userPreferredCurrency, setUserPreferredCurrency] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [userTier, setUserTier] = useState(null);
  // (24 Oct 2026, 8.40a, decision 100) "Show the day of the week with event dates"
  // (users.show_weekday, on by default), from Settings › Preferences.
  const [showWeekday, setShowWeekday] = useState(true);
  const [onboardingTiers, setOnboardingTiers] = useState([]);
  // GA notification queue (Aug 2026) — pending poll notifications for
  // calendars this user owns (phase1_open, phase2_open, phase1_closed_zero_offers)
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [resolvingNotificationId, setResolvingNotificationId] = useState(null);
  // Loading states for buttons
  const [isCreatingCalendar, setIsCreatingCalendar] = useState(false);
  const [isAddingEvent, setIsAddingEvent] = useState(false);
  // const [isDeletingCalendar, setIsDeletingCalendar] = useState(false);

  // Authority-aware landing (Aug 2026) — plain-member flow, separate from
  // the owner/deputy `calendars` list above. Populated only when the
  // signed-in account has no owned/deputised calendars AND no authority
  // anywhere, but does belong to at least one calendar as a plain member.
  const [memberCalendars, setMemberCalendars] = useState([]);
  // (8 Oct 2026, 8.35) Bumped by reload-on-return so a member's hosting schedule reloads.
  const [memberHostBoardVersion, setMemberHostBoardVersion] = useState(0);
  const [currentMemberCalendar, setCurrentMemberCalendar] = useState(null);
  const [memberEvents, setMemberEvents] = useState([]);
  const [isLoadingMemberEvents, setIsLoadingMemberEvents] = useState(false);
  // (11 Sep 2026) Past-events / poll-history view for members — separate
  // list rather than merging into memberEvents, so the default "upcoming"
  // load stays cheap and the past list is only fetched when a member
  // actually asks to see it.
  const [memberPastEvents, setMemberPastEvents] = useState([]);
  const [isLoadingMemberPastEvents, setIsLoadingMemberPastEvents] = useState(false);
  const [showingPastEvents, setShowingPastEvents] = useState(false);
  // (11 Sep 2026) Column set for the past-events dish-history table —
  // returned by the poll-history endpoint itself, ordered to match the
  // template's own course order rather than alphabetically.
  const [pollHistoryCourses, setPollHistoryCourses] = useState([]);
  // Phase 1/2 poll page (Aug 2026) — which event's poll is currently open,
  // when the member taps the poll-status badge from member-calendar.
  const [pollPageEventUid, setPollPageEventUid] = useState(null);
  // (10 Sep 2026) Where PollResultsPage's Back button should return to —
  // it's reachable from both MemberCalendarStep's "Poll closed · View
  // responses" badge and, as of today, DashboardStep's new "View Results"
  // link on any event with poll history. onBack was previously hardcoded
  // to 'member-calendar', which would have sent a GA to a screen built
  // for plain members. Defaults to the original member-facing behaviour.
  const [pollResultsReturnStep, setPollResultsReturnStep] = useState('member-calendar');
  // (9 Sep 2026) Proxy-entry Pass 4 — set when a GA is answering a poll on
  // behalf of a member (via MembersTab's new "Answer poll" action), null
  // for the normal member self-service flow. { calendarId, memberId }.
  const [pollPageProxy, setPollPageProxy] = useState(null);

  // (9 Sep 2026) Entry point for GA-proxy poll answering — called from
  // MembersTab.jsx (via DashboardStep -> SettingsModal) once it's already
  // resolved which single open poll to answer (or the GA picked one from
  // its own small chooser when more than one was open).
  // (14 Sep 2026) Now also carries pollId — needed once a single event
  // can have more than one poll worth showing (a closed offer poll
  // alongside an open/closed confirm poll, for the late-entry override),
  // since eventUid alone can no longer disambiguate which one the GA
  // actually chose. PollPage.jsx uses it to fetch that exact poll rather
  // than re-resolving "whatever's open" for the event.
  // (24 Sep 2026) returnStep — where PollPage's Back goes afterwards. Defaults
  // to 'main' (MembersTab's original entry); the results page's "Answer for
  // them" passes 'poll-results' so the GA lands back on the updated totals.
  const handleAnswerPollForMember = ({ calendarId, memberId, eventUid, pollId, returnStep = 'main' }) => {
    // showSettings is shared App.js-level state, also used by PollPage's
    // own SettingsModal instance — without clearing it here, that second
    // instance pops back open on top of the freshly-opened proxy PollPage,
    // since nothing else resets it on this particular navigation path.
    setShowSettings(false);
    setSettingsInitialTab('profile');
    setPollPageProxy({ calendarId, memberId, pollId, returnStep });
    setPollPageEventUid(eventUid);
    setStep('poll-page');
  };

  // (10 Sep 2026) GA entry point into PollResultsPage from DashboardStep's
  // new "View Results" link — sets the return step so Back goes to the
  // dashboard, not member-calendar (see pollResultsReturnStep above).
  // (24 Sep 2026) Return to the event you left from, not the top, when
  // coming back from a poll or results page to the dashboard or a member's
  // calendar (e.g. back from the November event's results lands at the
  // November event). Each screen is unmounted while away, so on the way out
  // we note WHICH event card was used and how far down the screen it sat;
  // on the way back that card is put at the same height again.
  //
  // First version saved a pixel scroll position, which landed on the wrong
  // event: the hosting schedule above the events loads after the screen
  // appears, so the page is shorter at first and the same pixel count lands
  // further down. Anchoring to the card itself doesn't depend on what's
  // above it. The card is re-aligned for up to 2 seconds while things above
  // finish loading, and alignment stops at once if the user scrolls, taps or
  // presses a key, so it never fights them. Used once, then forgotten.
  const savedScrollRef = useRef({});
  const findEventCard = (eventUid) => {
    if (!eventUid) return null;
    const matches = document.querySelectorAll(`[data-event-uid="${CSS.escape(eventUid)}"]`);
    // The past-events view renders both a phone layout and a table, one hidden
    // by CSS: use whichever is actually displayed.
    for (const el of matches) if (el.offsetParent !== null) return el;
    return null;
  };
  const rememberScroll = (fromStep, eventUid) => {
    const card = findEventCard(eventUid);
    savedScrollRef.current[fromStep] = card
      ? { eventUid, top: card.getBoundingClientRect().top }
      : { y: window.scrollY };
  };
  useEffect(() => {
    const saved = savedScrollRef.current[step];
    if (!saved) return undefined;
    delete savedScrollRef.current[step];
    let stopped = false;
    let timer = null;
    const started = Date.now();
    const stop = () => { stopped = true; };
    const userEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    userEvents.forEach((ev) => window.addEventListener(ev, stop, { passive: true }));
    const align = () => {
      if (stopped) return;
      if (saved.eventUid) {
        const card = findEventCard(saved.eventUid);
        if (card) {
          const diff = card.getBoundingClientRect().top - saved.top;
          if (Math.abs(diff) > 1) window.scrollBy(0, diff);
        }
      } else {
        window.scrollTo(0, saved.y);
      }
      if (Date.now() - started < 2000) timer = setTimeout(align, 100);
    };
    const frame = requestAnimationFrame(align);
    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      if (timer) clearTimeout(timer);
      userEvents.forEach((ev) => window.removeEventListener(ev, stop));
    };
  }, [step]);

  const handleViewPollResultsFromDashboard = (eventUid) => {
    rememberScroll('dashboard', eventUid);
    setPollPageEventUid(eventUid);
    setPollResultsReturnStep('dashboard');
    setStep('poll-results');
  };

  // (12 Sep 2026) GA entry point for answering a currently-open poll as
  // themselves — same underlying flow a plain member uses from
  // MemberCalendarStep's onOpenPoll (normal, non-proxy PollPage), just
  // reachable from the Dashboard's own event card instead. Added because
  // the only route into a real self-answer was previously via Members ->
  // proxy-answer, which is built for answering ON BEHALF OF someone else
  // and carries "Answering on behalf of..." framing that's misleading for
  // a GA answering for themselves. Explicitly clears pollPageProxy in
  // case a prior proxy-answer session left it set — this path must never
  // run in proxy mode.
  const handleAnswerPollAsSelfFromDashboard = (eventUid) => {
    setShowSettings(false);
    setSettingsInitialTab('profile');
    setPollPageProxy(null);
    rememberScroll('dashboard', eventUid);
    setPollPageEventUid(eventUid);
    setPollResultsReturnStep('dashboard');
    setStep('poll-page');
  };

  // (10 Sep 2026) Refetches and re-transforms the current calendar's events
  // — same shape as the other transform blocks in this file (including the
  // poll field added the same day) — so DashboardStep's new Open Phase 1 /
  // Close Poll Now actions can refresh poll-status badges without a full
  // page reload. Deliberately not deduplicated against the other four
  // near-identical transform blocks elsewhere in this file — out of scope
  // for this change.
  const refreshDashboardEvents = async () => {
    if (!currentCalendar) return;
    const startSeq = eventsSeqRef.current; // (11 Oct 2026, 8.41)
    try {
      const refreshResponse = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/events`);
      if (refreshResponse.ok) {
        const eventsData = await refreshResponse.json();
        const transformedEvents = eventsData.map(evt => ({
          id: evt.event_uid,
          title: evt.title,
          description: evt.description,
          location: evt.location,
          date: evt.event_date,
          time: evt.start_time,
          endTime: evt.end_time,
          created: evt.created_at,
          eventUid: evt.event_uid,
          start_timezone: evt.start_timezone || null,
          end_timezone: evt.end_timezone || null,
          hostMemberId: evt.host_member_id || null, // (30 Sep 2026, H17)
          historyIndexValue: evt.history_index_value || null, // (9 Oct 2026, 8.19/8.20, decision 65)
          hostLocation: evt.hostLocation || null,   // (30 Sep 2026, H17)
          poll: evt.poll || null,
          hasPollHistory: evt.hasPollHistory || false,
          pollOpensOn: evt.pollOpensOn || null, // (23 Oct 2026, 8.43, decision 112)
          pollSuppressed: evt.poll_suppressed === true, // (24 Oct 2026, decision 131)
          eventAdmin: evt.eventAdmin || null, // (26 Oct 2026, decision 145)
          waLog: []
        }));
        try {
          const logRes = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/whatsapp-log`);
          if (logRes.ok) {
            const logData = await logRes.json();
            transformedEvents.forEach(e => {
              e.waLog = logData.filter(l => l.event_uid === e.id).sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
            });
          }
        } catch (_) {}
        // (11 Oct 2026, 8.41) Something changed the list while this was loading.
        if (eventsSeqRef.current !== startSeq) return;
        setEvents(transformedEvents);
      }
    } catch (err) {
      console.error('Error refreshing events:', err);
    }
  };

  // (24 Oct 2026, decision 131) Poll dates for an event that is not moving
  // (calendarRoutes.js POST .../poll-dates). Opening asks the server for the event's
  // timetable; an answer is sent back the same way. The window stays open on an error.
  const pollDatesUrl = (eventUid) => `${API_BASE_URL}/api/calendars/${currentCalendar.id}/events/${eventUid}/poll-dates`;
  const handleOpenPollDates = async (event) => {
    if (!currentCalendar || !event) return;
    try {
      const response = await authenticatedFetch(pollDatesUrl(event.eventUid), { method: 'POST', body: JSON.stringify({}) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.timetable) {
        showStatus(data.error || 'Could not load the poll dates', 'error');
        refreshDashboardEvents();
        return;
      }
      setPollDatesQuestion({ eventUid: event.eventUid, title: event.title, mode: data.mode, timetable: data.timetable });
    } catch (err) {
      console.error('Error loading poll dates:', err);
      showStatus('Could not load the poll dates', 'error');
    }
  };
  const handlePollDatesAnswer = async (answer) => {
    const q = pollDatesQuestion;
    const plan = answer && answer.pollPlan;
    if (!q || !plan || !currentCalendar) return;
    if (plan.action === 'keep' && q.mode === 'stay') {
      setPollDatesQuestion(null);
      showStatus('The poll dates are unchanged.', 'success');
      return;
    }
    try {
      const response = await authenticatedFetch(pollDatesUrl(q.eventUid), { method: 'POST', body: JSON.stringify({ pollPlan: plan }) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showStatus(data.error || 'Could not save the poll dates', 'error');
        return;
      }
      setPollDatesQuestion(null);
      showStatus(data.note || 'Poll dates saved.', 'success', 'long');
      await refreshDashboardEvents();
    } catch (err) {
      console.error('Error saving poll dates:', err);
      showStatus('Could not save the poll dates', 'error');
    }
  };

  // (6 Oct 2026, 8.1) quiet: used by the reload-on-return below — no
  // "Loading..." state, and a failed reload keeps the list already shown
  // rather than blanking it.
  const loadMemberEvents = async (calendarId, { quiet = false, calendarName = null } = {}) => {
    if (!quiet) setIsLoadingMemberEvents(true);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${calendarId}/member-events`);
      if (response.ok) {
        const data = await response.json();
        setMemberEvents(data);
      } else if (response.status === 403) {
        // (8 Oct 2026, 8.35) Refused: check whether this person has been removed.
        checkStillMember(calendarId, calendarName);
      } else if (!quiet) {
        setMemberEvents([]);
      }
    } catch (error) {
      console.error('Error loading member events:', error);
      if (!quiet) setMemberEvents([]);
    } finally {
      if (!quiet) setIsLoadingMemberEvents(false);
    }
  };

  // (11 Sep 2026) Now backed by poll-history rather than
  // member-events?past=true — see memberCalendarRoutes.js. Returns both
  // the per-event dish data and the course column set together, since the
  // table needs both to render.
  const loadMemberPastEvents = async (calendarId) => {
    setIsLoadingMemberPastEvents(true);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${calendarId}/poll-history`);
      if (response.ok) {
        const data = await response.json();
        setMemberPastEvents(data.events || []);
        setPollHistoryCourses(data.courses || []);
      } else {
        setMemberPastEvents([]);
        setPollHistoryCourses([]);
      }
    } catch (error) {
      console.error('Error loading poll history:', error);
      setMemberPastEvents([]);
      setPollHistoryCourses([]);
    } finally {
      setIsLoadingMemberPastEvents(false);
    }
  };

  const handleToggleMemberPastEvents = () => {
    const next = !showingPastEvents;
    setShowingPastEvents(next);
    if (next && currentMemberCalendar) {
      loadMemberPastEvents(currentMemberCalendar.id);
    }
  };

  const enterMemberCalendar = (cal) => {
    setCurrentMemberCalendar(cal);
    setStep('member-calendar');
    setShowingPastEvents(false);
    loadMemberEvents(cal.id, { calendarName: cal.name });
  };

  // (8 Oct 2026, 8.35, decisions 30 and 31) A member's request was refused (403).
  // Ask landing-context, the same check used at sign-in, whether this calendar is
  // still theirs. Only if it is not: clear it, say so once (decision 30), and go to
  // their remaining calendar(s), or the "No calendars" screen (decision 31). Any
  // other refusal, or a failed check, changes nothing on screen.
  const removalCheckRef = useRef(false);
  const checkStillMember = async (calendarId, calendarName = null) => {
    if (!calendarId || removalCheckRef.current) return;
    removalCheckRef.current = true;
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/users/landing-context`);
      if (!res.ok) return;
      const landing = await res.json();
      const stillMine = [...(landing.memberCalendars || []), ...(landing.authorityCalendars || [])]
        .some((c) => c && c.id === calendarId);
      if (stillMine) return;
      const name = calendarName
        || (currentMemberCalendar && currentMemberCalendar.id === calendarId ? currentMemberCalendar.name : null)
        || ((memberCalendars || []).find((c) => c.id === calendarId) || {}).name
        || 'this calendar';
      console.log(`[8.35] no longer a member of ${name}; leaving it`);
      setCurrentMemberCalendar(null);
      setMemberEvents([]);
      setMemberPastEvents([]);
      setShowingPastEvents(false);
      const remaining = landing.memberCalendars || [];
      setMemberCalendars(remaining);
      if (landing.hasAuthority) {
        // Still an administrator elsewhere: start again from the usual landing.
        window.location.reload();
        return;
      }
      showStatus(`You're no longer a member of ${name}. If you think this is a mistake, please contact the administrator.`, 'error', 10000);
      if (remaining.length === 1) enterMemberCalendar(remaining[0]);
      else if (remaining.length > 1) setStep('choose-calendar');
      else setStep('no-access');
    } catch (_) {
      // Leave the screen as it is.
    } finally {
      removalCheckRef.current = false;
    }
  };

  // DEBUG: Log renders to catch black screen issue


useEffect(() => {
    
   // Capture token from URL (magic link redirect)
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

      if (token) {
        setAuthToken(token);

        // Decode JWT to extract email
        const email = decodeEmailFromToken(token);
        if (email) {
          setUserEmail(email);
          // Save to recent emails list, most-recent first.
          // (26 Sep 2026) One entry per address — ignoring capitals and spaces
          // — and up to 8 (was 5, and case-sensitive).
          try {
            const norm = (x) => String(x || '').trim().toLowerCase();
            const me = norm(email);
            const existing = JSON.parse(localStorage.getItem('saved_emails') || '[]').map(norm).filter(Boolean);
            const updated = [me, ...existing.filter((e, i, arr) => e !== me && arr.indexOf(e) === i)].slice(0, 8);
            localStorage.setItem('saved_emails', JSON.stringify(updated));
          } catch (_) {}
        }

        window.history.replaceState({}, document.title, window.location.pathname);
        showStatus('Signed in successfully!', 'success');
        // (11 Oct 2026, 8.4) Offer the sign-in to a waiting "Email sent!" tab.
        handOverSignIn(email);
      } else {
        // (1 Oct 2026, B1) If this tab was just signed out by the session
        // guard (see top of file), say why.
        try {
          const reason = sessionStorage.getItem(SIGNIN_NOTICE_KEY);
          if (reason) {
            sessionStorage.removeItem(SIGNIN_NOTICE_KEY);
            if (reason === 'handover') showStatus(SIGNIN_NOTICES.handover, 'success');
            else if (SIGNIN_NOTICES[reason]) showStatus(SIGNIN_NOTICES[reason], 'info', 'long');
          }
        } catch (_) {}

        // No fresh URL token (a reload or a later visit, not straight off
        // the magic link) — fall back to decoding email from whatever
        // token is already stored, so "Signed in as" doesn't go blank.
        const storedToken = getAuthToken();
        if (storedToken) {
          const email = decodeEmailFromToken(storedToken);
          if (email) setUserEmail(email);
        }
      }
    
    // Load saved emails — pre-fill input with most recent
    const savedEmailsRaw = localStorage.getItem('saved_emails');
    // (26 Sep 2026) Tidied on load too, so an older list with duplicates or
    // capitals shows each address once.
    const savedEmails = (savedEmailsRaw ? JSON.parse(savedEmailsRaw) : [])
      .map((x) => String(x || '').trim().toLowerCase())
      .filter((e, i, arr) => e && arr.indexOf(e) === i);
    // Backwards-compat: fall back to legacy key if no array yet
    if (savedEmails.length === 0) {
      const legacy = localStorage.getItem('last_login_email');
      if (legacy) savedEmails.push(legacy);
    }
    setSavedEmails(savedEmails);
    if (savedEmails.length > 0) setLoginEmail(savedEmails[0]);
    
    // Load user's calendars if authenticated
    const loadCalendars = async () => {
      const authToken = localStorage.getItem('auth_token');
      
      if (!authToken) {
        setIsAppLoading(false);
        return;
      }
      
      try {

        // (Sep 2026) Universal profile-completion gate — checked before ANY
        // routing branch below, so it applies equally to an account that
        // already owns calendars (which otherwise skips landing-context
        // entirely and goes straight to 'main') and to a calendar-less
        // account. Deliberately its own early fetch+return rather than
        // folded into the landing-context call further down, since that
        // call only happens on the no-owned-calendars path.
        // (22 Sep 2026) Renamed needsPreferredName -> needsProfileCompletion
        // to match landingService.js — now covers First/Last name too, per
        // Neil's decision, not just preferred_name.
        try {
          const nameCheckRes = await fetch(`${API_BASE_URL}/api/users/landing-context`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            }
          });
          if (nameCheckRes.ok) {
            const nameCheck = await nameCheckRes.json();
            setIsSA(Boolean(nameCheck.isSA));
            setRaRegions(nameCheck.raRegions || []);
            setAuthorityCalendars(nameCheck.authorityCalendars || []);
            setCanCreateCalendar(Boolean(nameCheck.canCreateCalendar));
            setIsSecondaryOnly(Boolean(nameCheck.isSecondaryOnly));
            // (28 Oct 2026, decision 154) Kept for an administrator too, so the calendar
            // drop-down can list the calendars where they are only a member (landing-
            // context already leaves out those they administer).
            setMemberCalendars(nameCheck.memberCalendars || []);
            if (nameCheck.needsProfileCompletion) {
              setStep('needs-profile-completion');
              setIsAppLoading(false);
              return;
            }
          }
        } catch (_) {
          // Non-fatal — if this check itself fails, fall through to normal
          // routing rather than stranding the user on a blank screen.
        }

        // Fetch calendars directly without using authenticatedFetch
        const response = await fetch(`${API_BASE_URL}/api/calendars`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to load calendars');
        }
        
        const calendarsData = await response.json();
        
        if (calendarsData.length > 0) {
          // Transform backend data to frontend format
          const transformedCalendars = calendarsData.map(cal => ({
            id: cal.id,
            short_code: cal.short_code,
            name: cal.name,
            description: cal.description,
            timezone: cal.timezone || 'Europe/London',
            created: cal.created_at,
            is_deputy: cal.is_deputy || false,
            owner_email: cal.owner_email,
            // (20 Sep 2026) Host schedule board on/off (calendars.host_board_enabled).
            host_board_enabled: cal.host_board_enabled === true,
            // (9 Oct 2026, 8.19/8.20, decisions 52, 64-67) The calendar history's settings.
            history_enabled: cal.history_enabled === true,
            history_keyword: cal.history_keyword || '',
            history_index_label: cal.history_index_label || '',
            history_show_menu: cal.history_show_menu === true,
            // (26 Oct 2026, decisions 145 and 146) Event Administrators; hidden dishes and
            // when members see them. Read by Settings › Calendar Options.
            ea_enabled: cal.ea_enabled === true,
            hide_dishes: cal.hide_dishes === true,
            dish_reveal: cal.dish_reveal || 'event_start',
            // (26 Sep 2026) H1: this calendar's own guide (calendars.guide_url,
            // e.g. /guides/HotClub-guide-E-05.pdf), shown at the top of the
            // Guide window. Null = general guide only.
            guide_url: cal.guide_url || null,
            // (23 Sep 2026) Per-calendar limits (read-only for GAs), shown on
            // SettingsModal's Limits tab. Previously dropped by this field
            // whitelist even though GET /api/calendars returns them.
            max_events_per_period: cal.max_events_per_period ?? null,
            event_period_days: cal.event_period_days ?? null,
            max_advance_days: cal.max_advance_days ?? null,
            max_members: cal.max_members ?? null,
            owner_first_name: cal.owner_first_name || null,
            owner_last_name: cal.owner_last_name || null,
            owner_preferred_name: cal.owner_preferred_name || null,
            subscriptionUrl: `${API_BASE_URL}/api/subscriptions/${cal.id}/feed.ics`,
            webcalUrl: `webcal://${API_BASE_URL.replace('https://', '').replace('http://', '')}/api/subscriptions/${cal.id}/feed.ics`,
            shareLink: window.location.origin + window.location.pathname + '?cal=' + cal.id
          }));
          
          setCalendars(transformedCalendars);
          
          // Set the first calendar as current
          const firstCalendar = transformedCalendars[0];
          setCurrentCalendar(firstCalendar);
          setCalendarTimezone(firstCalendar.timezone || 'Europe/London');
          
          // Load events for first calendar
          const eventsResponse = await fetch(`${API_BASE_URL}/api/calendars/${firstCalendar.id}/events`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            }
          });
          
          if (eventsResponse.ok) {
            const eventsData = await eventsResponse.json();
            const transformedEvents = eventsData.map(evt => ({
              id: evt.event_uid,
              title: evt.title,
              description: evt.description,
              location: evt.location,
              date: evt.event_date,
              time: evt.start_time,
              endTime: evt.end_time,
              created: evt.created_at,
              eventUid: evt.event_uid,
              // (1 Oct 2026) Were missing from this first-load copy only, so
              // the administrator's cards had no Directions (H17 Part 2) and
              // the event form opened with no host chosen until some other
              // action refreshed the list. Now the same as the other copies.
              start_timezone: evt.start_timezone || null,
              end_timezone: evt.end_timezone || null,
              hostMemberId: evt.host_member_id || null, // (30 Sep 2026, H17)
          historyIndexValue: evt.history_index_value || null, // (9 Oct 2026, 8.19/8.20, decision 65)
              hostLocation: evt.hostLocation || null,   // (30 Sep 2026, H17)
              poll: evt.poll || null,
          hasPollHistory: evt.hasPollHistory || false,
          pollOpensOn: evt.pollOpensOn || null, // (23 Oct 2026, 8.43, decision 112)
          pollSuppressed: evt.poll_suppressed === true, // (24 Oct 2026, decision 131)
          eventAdmin: evt.eventAdmin || null, // (26 Oct 2026, decision 145)
              waLog: []
            }));
            try {
              const logRes = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${firstCalendar.id}/whatsapp-log`);
              if (logRes.ok) {
                const logData = await logRes.json();
                transformedEvents.forEach(e => {
                  e.waLog = logData.filter(l => l.event_uid === e.id).sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
                });
              }
            } catch (_) {}
            setEvents(transformedEvents);
          } else {
            // (11 Sep 2026) Previously silently ignored — a non-ok response
            // here (e.g. 404 when resolveCalendarAccess finds no active
            // handoff for a ga_standin who is eligible but not currently
            // active) left events empty with no error, indistinguishable
            // from a genuinely empty calendar. Surface it instead.
            console.error('Failed to load events:', eventsResponse.status);
            setEvents([]);
            showStatus(
              eventsResponse.status === 404
                ? 'Could not load events — you may not currently hold active authority for this calendar.'
                : 'Failed to load events. Please try refreshing.',
              'error',
              4000
            );
          }
          
        // Change to main view
          setStep('main');
          } else {
            // No owned/deputised calendars — check landing context for
            // authority elsewhere (e.g. a region-scoped SA who hasn't
            // created a calendar yet) or plain membership, before falling
            // back to the old subscription-based routing. This replaces
            // the routing decision that previously sent EVERY calendar-less
            // account straight to create-calendar based on tier alone,
            // regardless of whether they actually held any authority.
            try {
              const landingResponse = await authenticatedFetch(`${API_BASE_URL}/api/users/landing-context`);
              if (landingResponse.ok) {
                const landing = await landingResponse.json();

                if (landing.hasAuthority) {
                  // Holds GA/SA/GSA authority somewhere but /api/calendars
                  // returned nothing owned/deputised yet (e.g. a region SA
                  // who hasn't created a calendar in their region) — they're
                  // allowed to create one.
                  setStep('create-calendar');
                } else if (landing.memberCalendars && landing.memberCalendars.length > 0) {
                  // Plain member only, no authority anywhere.
                  setMemberCalendars(landing.memberCalendars);
                  if (landing.memberCalendars.length === 1) {
                    // Skip the picker — straight into their one calendar.
                    enterMemberCalendar(landing.memberCalendars[0]);
                  } else {
                    setStep('choose-calendar');
                  }
                } else {
                  // (22 Sep 2026) Was: fetch /api/users/subscription and
                  // route to create-calendar if it returned ANY tier_name
                  // — that's true for every account regardless of role,
                  // so it wrongly sent a plain member with no roles at all
                  // to a form the backend would then reject. Now uses
                  // landing.canCreateCalendar, the same region-scoped 'ga'
                  // row calendarRoutes.js's create route itself requires
                  // (see landingService.js) — the subscription fetch is no
                  // longer needed for this decision.
                  //
                  // (22 Sep 2026, Neil's decision) This branch means the
                  // account genuinely has no authority, no membership, and
                  // no creation right anywhere — confirmed live (the
                  // "New member"/"Lobby" test account) that OnboardingStep
                  // was landing them on a self-serve "Choose a Plan"
                  // signup screen that predates the BETA_ALLOWLIST/invite-
                  // only model and leads nowhere real in it. Routes to
                  // 'no-access' instead, the same "not authorised, contact
                  // your Group Administrator" message used at the sign-in
                  // gate (authRoutes.js), for consistency. OnboardingStep
                  // itself is untouched and still used below for the two
                  // genuinely-technical failure fallbacks (landing-context
                  // request failing, or throwing) — those are transient
                  // problems, not an authorization rejection, so they
                  // deliberately keep the old behaviour rather than
                  // showing "not authorised" for what might just be a
                  // dropped request.
                  if (landing.canCreateCalendar) {
                    setStep('create-calendar');
                  } else {
                    setStep('no-access');
                  }
                }
              } else {
                // landing-context itself failed — don't strand the user on
                // a blank screen, fall back to onboarding same as any other
                // failure path below.
                setStep('onboarding');
              }
            } catch {
              setStep('onboarding');
            }
          }
      } catch (error) {
        console.error('Error loading calendars:', error);
        // Stay on welcome screen if error
      } finally {
        setIsAppLoading(false);
      }
    };
    
    loadCalendars();
  // authenticatedFetch is defined at module level (stable reference — no useCallback needed)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCalendars, setCurrentCalendar, setEvents, setStep]);

// Fetch user's tier limits
useEffect(() => {
  const fetchUserTier = async () => {
    if (!isAuthenticated()) return;
    
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/users/subscription`);
      if (response.ok) {
        const data = await response.json();
        setUserTier(data);
        // Fetch preferred_currency from preferences endpoint
        try {
          const settingsRes = await authenticatedFetch(`${API_BASE_URL}/api/users/preferences`);
          if (settingsRes.ok) {
            const settings = await settingsRes.json();
            setUserPreferredCurrency(settings.preferred_currency || null);
            setFirstName(settings.first_name || '');
            setLastName(settings.last_name || '');
            setPreferredName(settings.preferred_name || '');
            // (26 Sep 2026) This person's message times, if set.
            if (Number(settings.message_seconds) > 0) messageTimesRef.current.normal = Number(settings.message_seconds);
            if (Number(settings.error_message_seconds) > 0) messageTimesRef.current.error = Number(settings.error_message_seconds);
            setShowWeekday(settings.show_weekday !== false); // (24 Oct 2026, 8.40a)
          }
        } catch (_) {}
      }
    } catch (error) {
      console.error('Error fetching tier:', error);
    }
  };
  
  fetchUserTier();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [calendars.length]);

// (22 Sep 2026, Neil's decision) Idle timeout — sign out automatically
// after 20 minutes with no user activity, so a session left open on a
// shared or unattended device doesn't stay signed in indefinitely.
// Deliberately client-side only, same mechanism as the existing Sign Out
// buttons (clearAuthToken + reload) — this does NOT invalidate the JWT
// server-side (confirmed live 21 Sep: plain sign-out never has either),
// so a captured token could still be replayed directly against the API
// until it expires naturally (7 days) regardless of this timer.
//
// REVISED same day, after a live iPhone test: signed in at 09:38, left
// completely untouched, still fully signed in and able to open a poll at
// 10:02 (24 minutes later) — no timeout had fired. Root cause: the
// original version armed a single setTimeout and reset it on every
// activity event. iOS Safari suspends JS timers for a backgrounded/
// locked tab, so the pending setTimeout never got to run while the phone
// was locked; and the very tap that brings the tab back to the
// foreground is ITSELF one of the activity events, which called
// clearTimeout + started a fresh 20-minute window before the overdue
// callback ever had a chance to fire — silently resetting the clock on
// return as if no time had passed. Fixed by tracking actual elapsed wall
// time (lastActivityAt) instead of relying on an uninterrupted timer:
// a periodic check handles the ordinary foreground-idle case, and an
// explicit visibilitychange check runs the instant the tab becomes
// visible again, BEFORE that same returning tap's own activity listener
// gets to update lastActivityAt — closing the exact race above.
useEffect(() => {
  // (1 Oct 2026, B1) Activity is now shared between tabs through
  // localStorage (written at most every 15 seconds), so a background tab
  // left idle no longer signs out the tab being used — all tabs share one
  // sign-in. The same 30-second check also ends a sign-in whose own expiry
  // time has passed. Both end through endSession (top of file), which
  // shows the reason on the sign-in screen.
  const CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds
  const SHARE_EVERY_MS = 15 * 1000;

  let lastActivityAt = Date.now();
  let lastShared = 0;
  const shareActivity = () => {
    lastShared = lastActivityAt;
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(lastActivityAt)); } catch (_) {}
  };
  const markActivity = () => {
    lastActivityAt = Date.now();
    if (lastActivityAt - lastShared >= SHARE_EVERY_MS) shareActivity();
  };
  if (isAuthenticated()) shareActivity();

  const checkIdle = () => {
    if (!isAuthenticated()) return;
    if (tokenHasExpired(getAuthToken())) {
      endSession('expired');
      return;
    }
    const latest = Math.max(lastActivityAt, sharedLastActivity());
    if (Date.now() - latest >= SESSION_IDLE_MS) {
      endSession('idle');
    }
  };

  const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
  activityEvents.forEach((evt) => window.addEventListener(evt, markActivity));

  const intervalId = setInterval(checkIdle, CHECK_INTERVAL_MS);

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') checkIdle();
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    clearInterval(intervalId);
    activityEvents.forEach((evt) => window.removeEventListener(evt, markActivity));
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// Auto-close Add Event form if limit reached
useEffect(() => {
  const eventLimit = userTier?.max_events_per_calendar;
  const eventCount = events.length;
  const atLimit = eventLimit !== null && eventCount >= eventLimit;
  
  if (atLimit && showAddEvent) {
    setShowAddEvent(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [events.length, userTier]);

  // Fetch tiers for onboarding screen
  useEffect(() => {
    if (step === 'onboarding' && onboardingTiers.length === 0) {
      fetch(`${API_BASE_URL}/api/tiers?currency=GBP`, { cache: 'no-store' })
        .then(r => r.json())
        .then(data => setOnboardingTiers(Array.isArray(data?.tiers) ? data.tiers : []))
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Fetch pending GA poll notifications for the calendar currently being
  // viewed. (14 Sep 2026) Previously fetched every pending notification
  // across every calendar this GA owns, with no calendar_id filter at
  // all — so a GA holding more than one calendar would see another
  // calendar's notifications bleed into whichever dashboard they were
  // actually looking at. Now scoped to currentCalendar.id, and refetches
  // on calendar switch (previously only refired when the number of
  // calendars changed, so switching between two already-loaded
  // calendars didn't refresh this list either).
  //
  // (14 Sep 2026, same day) Pulled out of the effect below into its own
  // named function so it can also be called directly after a manual
  // scheduler run — previously "Run Scheduler" only showed a toast and
  // never refetched anything, so a genuinely new reminder notification
  // (or an event's poll-status badge) stayed stale until a hard refresh.
  const fetchPendingNotifications = async () => {
    if (!isAuthenticated() || !currentCalendar) return;
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/notifications/pending?calendarId=${currentCalendar.id}`);
      if (response.ok) {
        const data = await response.json();
        setPendingNotifications(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching pending notifications:', error);
    }
  };

  useEffect(() => {
    fetchPendingNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCalendar?.id]);

  const resolveNotification = async (notificationId, action) => {
    setResolvingNotificationId(notificationId);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/notifications/${notificationId}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ action })
      });
      if (response.ok) {
        setPendingNotifications(prev => prev.filter(n => n.id !== notificationId));
        showStatus(action === 'sent' ? 'Marked as sent' : 'Dismissed', 'success');
      } else if (response.status === 404) {
        // (29 Sep 2026, H12) The notice no longer exists on the server — typically
        // its event was deleted (which removes its notices) while this list was
        // still on screen. Nothing left to do: drop it quietly, then refetch so
        // the bell matches the server (a notice that does still exist comes back).
        setPendingNotifications(prev => prev.filter(n => n.id !== notificationId));
        fetchPendingNotifications();
      } else {
        const error = await response.json();
        showStatus(error.error || 'Failed to update notification', 'error');
      }
    } catch (error) {
      showStatus('Failed to update notification', 'error');
    } finally {
      setResolvingNotificationId(null);
    }
  };

  // (20 Sep 2026) Host board tasks for the Notifications bell: the daily job asks the GA to
  // send a "please reconfirm you can host" request to the chosen host, and leaves her notes
  // (host confirmed by default, host stepped down, a month needing her decision). Only the
  // currently active holder is ever sent any (the server returns an empty list otherwise).
  const fetchHostBoardTasks = async () => {
    // (11 Oct 2026, 8.5, decision 45) A stand-in standing in sees the bell too (the
    // server sends tasks only to whoever currently holds authority).
    if (!isAuthenticated() || !currentCalendar || !currentCalendar.host_board_enabled) {
      setHostBoardTasks([]);
      return;
    }
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/host-board/tasks`);
      if (response.ok) {
        const data = await response.json();
        setHostBoardTasks(Array.isArray(data.tasks) ? data.tasks : []);
      }
    } catch (error) {
      console.error('Error fetching host board tasks:', error);
    }
  };

  useEffect(() => {
    fetchHostBoardTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCalendar?.id, currentCalendar?.host_board_enabled, hostBoardVersion]);

  // ============================================
  // RELOAD WHEN THE TAB COMES BACK INTO VIEW (6 Oct 2026, 8.1)
  // ============================================
  // A page left open did not show other people's changes (members'
  // answers, hosting offers, new bell notices) until F5 — often mistaken
  // for a bug in testing. Now, when the tab becomes visible again or the
  // window regains focus, the current screen quietly reloads:
  //   - administrator dashboard: events (with poll badges), the bell's poll
  //     notices, and the hosting schedule plus its bell tasks (both reload
  //     through hostBoardVersion);
  //   - member's calendar: the upcoming events list.
  // Skipped while the person is part-way through something (event form,
  // Settings, a confirmation or WhatsApp prompt), so nothing changes under
  // them; and while the session is ending (the idle check runs first on
  // the same visibilitychange and may be signing out). At most once every
  // 5 seconds, since focus and visibilitychange usually fire together.
  // The latest version of the reload is kept in a ref, so the listeners
  // are added once and never see stale screen state.
  const returnRefreshRef = useRef(null);
  returnRefreshRef.current = () => {
    if (!isAuthenticated() || sessionEnding) return;
    if (showAddEvent || showEditEvent || showSettings || whatsappNotifyModal ||
        showDeleteEventConfirm || showDeleteConfirm || showRenameCalendar || scheduleChoice || pollDatesQuestion) return;
    if (step === 'main' && currentCalendar) {
      refreshDashboardEvents();
      fetchPendingNotifications();
      setHostBoardVersion(v => v + 1);
    } else if (step === 'member-calendar' && currentMemberCalendar) {
      loadMemberEvents(currentMemberCalendar.id, { quiet: true, calendarName: currentMemberCalendar.name });
      // (8 Oct 2026, 8.35) The member's hosting schedule reloads too.
      setMemberHostBoardVersion(v => v + 1);
    }
  };

  useEffect(() => {
    const MIN_GAP_MS = 5000;
    let lastRun = 0;
    let timer = null;
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRun < MIN_GAP_MS) return;
      lastRun = Date.now();
      if (timer) clearTimeout(timer);
      // Short delay: lets the idle check (same event) sign out first if due.
      timer = setTimeout(() => {
        timer = null;
        if (returnRefreshRef.current) returnRefreshRef.current();
      }, 300);
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, []);

  const resolveBoardTask = async (task, action) => {
    setResolvingBoardTaskId(task.id);
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/api/calendars/${currentCalendar.id}/host-board/tasks/${task.id}/resolve`,
        { method: 'POST', body: JSON.stringify({ action }) }
      );
      if (response.ok) {
        setHostBoardTasks(prev => prev.filter(t => t.id !== task.id));
        setHostBoardVersion(v => v + 1); // sending a request stamps the month: refresh the board
        showStatus(action === 'sent' ? 'Marked as sent' : 'Dismissed', 'success');
      } else {
        const error = await response.json().catch(() => ({}));
        showStatus(error.error || 'Failed to update notification', 'error');
      }
    } catch (error) {
      showStatus('Failed to update notification', 'error');
    } finally {
      setResolvingBoardTaskId(null);
    }
  };

  // Opens WhatsApp with the prepared request and NO contact chosen: the GA picks the person
  // herself. Opened straight from the click (before any await) so pop-up blockers allow it.
  const handleSendBoardTask = (task) => {
    if (task.messageText) {
      window.open('https://wa.me/?text=' + encodeURIComponent(task.messageText), '_blank');
    }
    resolveBoardTask(task, 'sent');
  };

  // The fallback for a host who is not on WhatsApp: the server emails them.
  const handleEmailBoardTask = async (task) => {
    setResolvingBoardTaskId(task.id);
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/api/calendars/${currentCalendar.id}/host-board/tasks/${task.id}/email`,
        { method: 'POST' }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setHostBoardTasks(prev => prev.filter(t => t.id !== task.id));
        setHostBoardVersion(v => v + 1);
        showStatus(data.hostName ? `Email sent to ${data.hostName}` : 'Email sent', 'success');
      } else {
        showStatus(data.error || 'Could not send the email', 'error', 6000);
      }
    } catch (error) {
      showStatus('Could not send the email', 'error', 6000);
    } finally {
      setResolvingBoardTaskId(null);
    }
  };

  const handleSendMagicLink = async () => {
  if (!loginEmail) {
    showStatus('Please enter your email', 'error');
    return;
  }

  setIsSendingMagicLink(true);

  try {
    await requestMagicLink(loginEmail);
    // (30 Oct 2026, decision 161) Remember the address here too, in the tab where it was
    // typed, once the app has accepted it (a refused address throws above and is never
    // kept). Until now it was remembered only when the email's link opened, which on an
    // iPhone may be the mail app's own browser, whose storage Safari never sees. Same
    // list and rule as the link's tab: one entry per address, most recent first, up to 8.
    try {
      const norm = (x) => String(x || '').trim().toLowerCase();
      const me = norm(loginEmail);
      const existing = JSON.parse(localStorage.getItem('saved_emails') || '[]').map(norm).filter(Boolean);
      const updated = [me, ...existing.filter((e, i, arr) => e !== me && arr.indexOf(e) === i)].slice(0, 8);
      localStorage.setItem('saved_emails', JSON.stringify(updated));
      setSavedEmails(updated);
    } catch (_) {}
    magicLinkSentAtRef.current = Date.now(); // (11 Oct 2026, 8.4) the most recent waiting tab takes over
    setSignedInElsewhere(false);
    setMagicLinkSent(true);
    // (28 Oct 2026, decision 155) No pop-up: the green "Email sent!" panel says the same and
    // stays on screen. Errors keep their pop-up below.
  } catch (error) {
    // 6000ms (3× default) so beta-restricted message is readable
    showStatus(error.message, 'error', 6000);
  } finally {
    setIsSendingMagicLink(false);
  }
};

  // (11 Oct 2026, 8.4, decision 37) This tab, while on "Email sent!", answers a
  // link's tab that has just signed in with the same email (see handOverSignIn at
  // the top of the file). Told to take over: reload, which opens signed in, as the
  // sign-in is shared by every tab of the browser. Told to stay put: say so.
  // Answered but then told nothing (the closing tab's last word was lost): take
  // over anyway, the safer of the two outcomes.
  const signInWaitRef = useRef({ waiting: false, email: '' });
  signInWaitRef.current = {
    waiting: step === 'welcome' && magicLinkSent && !signedInElsewhere,
    email: (loginEmail || '').trim().toLowerCase(),
  };
  useEffect(() => {
    const channel = openSigninChannel();
    if (!channel) return undefined;
    const myId = newTabId();
    let fallback = null;
    const takeOver = () => {
      if (fallback) { clearTimeout(fallback); fallback = null; }
      try { sessionStorage.setItem(SIGNIN_NOTICE_KEY, 'handover'); } catch (_) {}
      window.location.reload();
    };
    channel.onmessage = (e) => {
      const m = e.data || {};
      if (m.type === 'signed-in') {
        const w = signInWaitRef.current;
        if (!w.waiting || !w.email || m.email !== w.email || !m.from) return;
        channel.postMessage({ type: 'waiting', to: m.from, from: myId, sentAt: magicLinkSentAtRef.current });
        if (fallback) clearTimeout(fallback);
        fallback = setTimeout(takeOver, SIGNIN_TAKEOVER_WAIT_MS);
      } else if (m.to === myId && m.type === 'take-over') {
        takeOver();
      } else if (m.to === myId && m.type === 'stay-put') {
        if (fallback) { clearTimeout(fallback); fallback = null; }
        setSignedInElsewhere(true);
      }
    };
    return () => {
      if (fallback) clearTimeout(fallback);
      channel.close();
    };
  }, []);

  const loadOnboardingTiers = async (currency) => {
    setTiersLoading(true);
    try {
      const cur = currency || onboardingCurrency || 'GBP';
      const response = await fetch(`${API_BASE_URL}/api/tiers?currency=${cur}`);
      const data = await response.json();
      setOnboardingTiersList(Array.isArray(data?.tiers) ? data.tiers : []);
    } catch (err) {
      console.error('Failed to load tiers:', err);
    } finally {
      setTiersLoading(false);
    }
  };

  const activateSubscription = async (tier_name) => {
    setActivatingTier(true);
    setActivatingTierName(tier_name);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/users/activate-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier_name })
      });
      const data = await response.json();
      if (!response.ok) {
        // 409 means subscription already exists — just navigate forward
        if (response.status === 409) {
          setStep('create-calendar');
          return;
        }
        showStatus(data.error || 'Failed to activate subscription', 'error');
        return;
      }
      showStatus('Subscription activated!', 'success');
      // Refresh userTier so Settings > Subscription tab shows the new plan
      try {
        const tierRes = await authenticatedFetch(`${API_BASE_URL}/api/users/subscription`);
        if (tierRes.ok) { const td = await tierRes.json(); setUserTier(td); }
      } catch (_) {}
      setStep('create-calendar');
    } catch (err) {
      showStatus('Failed to activate subscription', 'error');
    } finally {
      setActivatingTier(false);
      setActivatingTierName(null);
    }
  };

  const handleSelectTier = (tier) => {
    const freeTiers = ['free_weekly', 'free_monthly'];
    if (freeTiers.includes(tier.tier_name)) {
      activateSubscription(tier.tier_name);
    } else {
      setSelectedPaidTier(tier);
      setShowComingSoon(true);
    }
  };

const handleCreateCalendar = async () => {
  if (!calendarName) {
    showStatus('Please enter a calendar name', 'error');
    return;
  }

  setIsCreatingCalendar(true);

  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/api/calendars`, {
      method: 'POST',
      body: JSON.stringify({ 
        name: calendarName,
        description: calendarDescription,
        host_board_enabled: newCalendarHostBoard
      })
    });

    if (!response.ok) {
      const error = await response.json();
      
      // Check if it's a tier limit error
      if (error.error === 'Calendar limit reached') {
        showStatus(error.message, 'error', 5000);
        setIsCreatingCalendar(false);
        return;
      }
      
      throw new Error(error.error || 'Failed to create calendar');
    }

    const result = await response.json();
    
    const calendar = {
      id: result.id,
      short_code: result.short_code,
      name: calendarName,
      description: calendarDescription,
      host_board_enabled: newCalendarHostBoard,
      created: new Date().toISOString(),
      subscriptionUrl: result.subscriptionUrl,
      webcalUrl: result.webcalUrl,
      shareLink: window.location.origin + window.location.pathname + '?cal=' + result.id
    };
    
    const calData = { 
      ...calendar, 
      events: [], 
      members: ['You (Owner)'] 
    };
    
    await window.storage.set('calendar:' + calendar.id, JSON.stringify(calData));
    
    const updatedCalendars = [...calendars, calendar];
    setCalendars(updatedCalendars);
    
    await window.storage.set('user-calendars', JSON.stringify({
      calendars: updatedCalendars,
      updated: new Date().toISOString()
    }));
    
    
    setCurrentCalendar(calData);
    setEventsChanged([]);
    setCalendarName('');
    setCalendarDescription('');
    setNewCalendarHostBoard(false);
    setStep('main');
    
    showStatus('Calendar created successfully!', 'success');
  } catch (error) {
    console.error('Error creating calendar:', error);
    // 6s, not the 2s default — this can carry an authority/permission
    // message (e.g. "No region assigned to this account") that's easy to
    // miss at 2s, and the form fields are left populated either way.
    showStatus(error.message, 'error', 6000);
  } finally {
    setIsCreatingCalendar(false);
  }
};

const handleDeleteCalendar = async () => {
  if (!currentCalendar) return;

  // Close dialog immediately to prevent showing wrong calendar name
  setShowDeleteConfirm(false);
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to delete calendar');
    }

    // Remove from calendars list
    const updatedCalendars = calendars.filter(cal => cal.id !== currentCalendar.id);
    setCalendars(updatedCalendars);
    
    // If there are other calendars, switch to the first one
    if (updatedCalendars.length > 0) {
      const firstCal = updatedCalendars[0];
      setCurrentCalendar(firstCal);
      
      // Load events for new calendar
      const eventsResponse = await fetch(`${API_BASE_URL}/api/calendars/${firstCal.id}/events`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (eventsResponse.ok) {
        const eventsData = await eventsResponse.json();
        const transformedEvents = eventsData.map(evt => ({
          id: evt.event_uid,
          title: evt.title,
          description: evt.description,
          location: evt.location,
          date: evt.event_date,
          time: evt.start_time,
          endTime: evt.end_time,
          created: evt.created_at,
          eventUid: evt.event_uid,
          start_timezone: evt.start_timezone || null,
          end_timezone: evt.end_timezone || null,
          hostMemberId: evt.host_member_id || null, // (30 Sep 2026, H17)
          historyIndexValue: evt.history_index_value || null, // (9 Oct 2026, 8.19/8.20, decision 65)
          hostLocation: evt.hostLocation || null,   // (30 Sep 2026, H17)
          poll: evt.poll || null,
          hasPollHistory: evt.hasPollHistory || false,
          pollOpensOn: evt.pollOpensOn || null, // (23 Oct 2026, 8.43, decision 112)
          pollSuppressed: evt.poll_suppressed === true, // (24 Oct 2026, decision 131)
          eventAdmin: evt.eventAdmin || null, // (26 Oct 2026, decision 145)
          waLog: []
        }));
        try {
          const logRes = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${firstCal.id}/whatsapp-log`);
          if (logRes.ok) {
            const logData = await logRes.json();
            transformedEvents.forEach(e => {
              e.waLog = logData.filter(l => l.event_uid === e.id).sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
            });
          }
        } catch (_) {}
        setEventsChanged(transformedEvents);
      }
      
      showStatus('Calendar deleted successfully', 'success');

    } else {
      // No calendars left, go back to welcome
      setCurrentCalendar(null);
      setEventsChanged([]);
      setStep('welcome');
      showStatus('Calendar deleted successfully', 'success');

    }
    
  } catch (error) {
    console.error('Error deleting calendar:', error);
    showStatus('Error deleting calendar', 'error');
  }
};

const handleRenameCalendar = async () => {
  if (!currentCalendar || !renameCalendarInput.trim()) return;
  const oldName = currentCalendar.name;
  const newName = renameCalendarInput.trim();
  if (newName === oldName) {
    setShowRenameCalendar(false);
    return;
  }

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/calendars/${currentCalendar.id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName })
      }
    );

    if (!response.ok) throw new Error('Failed to rename calendar');

    // Update state
    const updatedCalendar = { ...currentCalendar, name: newName };
    setCurrentCalendar(updatedCalendar);
    setCalendars(prev => prev.map(c => c.id === currentCalendar.id ? updatedCalendar : c));
    setShowRenameCalendar(false);
    showStatus('Calendar renamed successfully', 'success');

    // Trigger WhatsApp notify
    setWhatsappNotifyModal({
      type: 'calendar_rename',
      subject: null,
      changes: `Calendar name changed from "${oldName}" to "${newName}"`,
      calendarName: oldName,
      calendarId: currentCalendar.id
    });
  } catch (error) {
    console.error('Error renaming calendar:', error);
    showStatus('Error renaming calendar', 'error');
  }
};

const handleSaveCalendarTimezone = async (newTzid) => {
  if (!currentCalendar || !newTzid) return;
  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/calendars/${currentCalendar.id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timezone: newTzid })
      }
    );
    if (!response.ok) throw new Error('Failed to save timezone');
    // Update local state so ICS feed and event forms reflect the change immediately
    const updated = { ...currentCalendar, timezone: newTzid };
    setCurrentCalendar(updated);
    setCalendars(prev => prev.map(c => c.id === currentCalendar.id ? updated : c));
    setCalendarTimezone(newTzid);
    showStatus('Calendar timezone saved', 'success');
  } catch (err) {
    console.error('Error saving timezone:', err);
    showStatus('Failed to save timezone', 'error');
  }
};

// (9 Oct 2026, decision 60) Settings > Calendar Options now saves with its footer button
// (CalendarOptionsTab.jsx does the saving). This keeps the app's copy of the calendar in
// step with what was saved: host_board_enabled (20 Sep 2026: turning it off only hides the
// board; its months are kept) and the history_* fields (8.19/8.20: turning it off only hides
// the History button). Replaces the two handlers that saved each switch at once.
const handleCalendarOptionsSaved = (fields) => {
  if (!currentCalendar || !fields) return;
  const updated = { ...currentCalendar, ...fields };
  setCurrentCalendar(updated);
  setCalendars(prev => prev.map(c => c.id === currentCalendar.id ? updated : c));
  // (26 Oct 2026, decision 145) The event cards show each event's Event Administrator.
  if (fields.ea_enabled !== undefined) refreshDashboardEvents();
};

// (20 Sep 2026) Host board -> Add Event. Opens the normal Add Event form pre-filled from a
// confirmed board month (date, title, host as location, notes). Times come from the
// calendar's most recent event, or the form's usual defaults if there is none. Saving
// the form links the month to the event (hostSlotId is sent by addEvent).
const handleStartEventFromBoard = (prefill) => {
  if (!currentCalendar || !prefill) return;
  const latest = [...events]
    .filter(e => e.time)
    .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  const startTime = (latest && formatTime(latest.time)) || getNextQuarterHour();
  const endTime = (latest && formatTime(latest.endTime)) || getEndTime(startTime);
  setEditingEvent(null);
  setShowEditEvent(false);
  setNewEvent({
    title: prefill.title || '',
    description: prefill.notes || '',
    location: prefill.location || '',
    date: prefill.date || '',
    time: startTime,
    endTime,
    // (5 Oct 2026, H18 Stage 1) The month's host (if any) is the event's host.
    hostMemberId: prefill.hostMemberId || ''
  });
  setEventUseCustomTz(false); setEventDiffEndTz(false); setEventStartTz(''); setEventEndTz('');
  setPendingHostSlotId(prefill.slotId || null);
  setShowAddEvent(true);
  setTimeout(() => {
    if (editEventRef.current) {
      const yOffset = -180; // clear the dashboard header
      const y = editEventRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, 50);
};

// (20 Sep 2026) Host board -> "Tell members via WhatsApp". Opens the usual WhatsApp
// prompt with a prepared "board updated" message; sending it (not skipping) resets
// the board's changes-since-last-told count (see handleSendWaNotification).
const handleAnnounceHostBoard = () => {
  if (!currentCalendar) return;
  setWhatsappNotifyModal({
    type: 'host_board_update',
    subject: '',
    changes: '',
    calendarName: currentCalendar.name,
    shortCode: currentCalendar.short_code,
    eventId: null,
    calendarId: null,
    boardCalendarId: currentCalendar.id
  });
};

const handleSwitchCalendar = async (calendarId) => {
  const selectedCalendar = calendars.find(cal => cal.id === calendarId);
  if (!selectedCalendar) return;
  
  setCurrentCalendar(selectedCalendar);
  setCalendarTimezone(selectedCalendar.timezone || 'Europe/London');

  // (14 Sep 2026) Fire-and-forget — records this as the last-viewed
  // calendar so the landing page defaults here next sign-in (see
  // landingService.js). Fires on every switch regardless of what's done
  // once there, per Neil's explicit choice. Deliberately not awaited and
  // not blocking the rest of this function — a failure here shouldn't
  // affect the calendar switch itself, just next time's default.
  authenticatedFetch(`${API_BASE_URL}/api/users/last-viewed-calendar`, {
    method: 'PATCH',
    body: JSON.stringify({ calendarId })
  }).catch(error => console.error('Error recording last-viewed calendar:', error));
  
  // Load events for selected calendar
  try {
    const response = await fetch(`${API_BASE_URL}/api/calendars/${calendarId}/events`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      }
    });
    
    if (response.ok) {
      const eventsData = await response.json();
      const transformedEvents = eventsData.map(evt => ({
        id: evt.event_uid,
        title: evt.title,
        description: evt.description,
        location: evt.location,
        date: evt.event_date,
        time: evt.start_time,
        endTime: evt.end_time,
        created: evt.created_at,
        eventUid: evt.event_uid,
        // (1 Oct 2026) Same omission as the first-load copy — see there.
        start_timezone: evt.start_timezone || null,
        end_timezone: evt.end_timezone || null,
        hostMemberId: evt.host_member_id || null, // (30 Sep 2026, H17)
          historyIndexValue: evt.history_index_value || null, // (9 Oct 2026, 8.19/8.20, decision 65)
        hostLocation: evt.hostLocation || null,   // (30 Sep 2026, H17)
        poll: evt.poll || null,
          hasPollHistory: evt.hasPollHistory || false,
          pollOpensOn: evt.pollOpensOn || null, // (23 Oct 2026, 8.43, decision 112)
          pollSuppressed: evt.poll_suppressed === true, // (24 Oct 2026, decision 131)
          eventAdmin: evt.eventAdmin || null, // (26 Oct 2026, decision 145)
        waLog: []
      }));
      try {
        const logRes = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${calendarId}/whatsapp-log`);
        if (logRes.ok) {
          const logData = await logRes.json();
          transformedEvents.forEach(e => {
            e.waLog = logData.filter(l => l.event_uid === e.id).sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
          });
        }
      } catch (_) {}
      setEventsChanged(transformedEvents);
    }
  } catch (error) {
    console.error('Error loading events:', error);
    showStatus('Error loading calendar events', 'error');
  }
};

// (28 Oct 2026, decision 154) One calendar drop-down on the dashboard and on the member
// page: choosing a calendar you administer shows its dashboard; choosing one where you are
// only a member ("member:{id}") shows its member page.
const handleChooseCalendar = (value) => {
  const v = String(value || '');
  if (v.startsWith('member:')) {
    const cal = (memberCalendars || []).find((c) => c.id === v.slice(7));
    if (cal) enterMemberCalendar(cal);
    return;
  }
  if (!calendars.some((c) => c.id === v)) return;
  setStep('main');
  if (!currentCalendar || currentCalendar.id !== v) handleSwitchCalendar(v);
};

// (28 Oct 2026, decision 153) "New Calendar" on a member's page (and on Choose a calendar)
// for someone with the right to create calendars who owns none yet, e.g. a member of one
// group appointed Group Administrator for another. They used to land on the member page
// with no way to reach Create Calendar. Cancel there returns to the page they came from.
const [createReturnStep, setCreateReturnStep] = useState(null);
const handleNewCalendarFromMember = (fromStep) => {
  setCalendarName('');
  setCalendarDescription('');
  setNewCalendarHostBoard(false);
  setCreateReturnStep(fromStep);
  setStep('create-calendar');
};

const handleCreateNewCalendar = () => {
  if (calendars.length === 0 && (!firstName.trim() || !lastName.trim())) {
    showStatus('Please complete your profile before creating your first calendar', 'error');
    setShowSettings(true);
    setSettingsInitialTab('profile');
    return;
  }
  setStep('create-calendar');
  // Reset form
  setCalendarName('');
  setCalendarDescription('');
};

// (6 Oct 2026, H18 Stage 2) planArg: the answer to a hosting-schedule question, e.g.
// { mode: 'new_row' | 'second' | 'one_off' | 'reopen' | 'link', slotId }. With
// deferNotify the WhatsApp prompt is returned instead of shown (used by "instead of
// it", which shows the delete prompt first). Called straight from the button too,
// where planArg is the click event: anything without a mode is ignored.
const addEvent = async (planArg) => {
  const plan = planArg && typeof planArg === 'object' && typeof planArg.mode === 'string' ? planArg : null;
  // "Add it to the hosting schedule only for now": no event yet, just a row.
  if (newEvent.scheduleOnly && currentCalendar?.host_board_enabled === true && !pendingHostSlotId && !editingEvent) {
    return addToScheduleOnly(plan);
  }
  if (!newEvent.title || !newEvent.date) {
    showStatus('Please enter event title and date', 'error');
    return null;
  }
  // (9 Oct 2026, 8.19/8.20, decisions 50, 65) An event in the history needs its index
  // value (Hot Club: the Country). A keyword left alone on the first line is dropped.
  const notesToSaveAdd = withoutBareKeyword(currentCalendar, newEvent.description);
  const indexMissingAdd = missingIndexMessage(currentCalendar, notesToSaveAdd, newEvent.historyIndexValue);
  if (indexMissingAdd) {
    showStatus(indexMissingAdd, 'error', 6000);
    return null;
  }

  setIsAddingEvent(true);

  try {
    const linkSlotId = plan && plan.mode === 'link' ? plan.slotId : pendingHostSlotId;
    const schedulePlan = plan && plan.mode !== 'link' ? { mode: plan.mode, slotId: plan.slotId || undefined } : undefined;
    const response = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/events`, {
      method: 'POST',
      body: JSON.stringify({
        title: newEvent.title,
        description: notesToSaveAdd,
        location: newEvent.location,
        date: newEvent.date,
        time: newEvent.time,
        endTime: newEvent.endTime,
        historyIndexValue: (newEvent.historyIndexValue || '').trim() || undefined, // (9 Oct 2026, decision 65)
        // (20 Sep 2026) Set when this form was opened from a host-board month; (6 Oct
        // 2026, H18 Stage 2) also the row freed by "instead of it".
        hostSlotId: linkSlotId || undefined,
        // (6 Oct 2026, H18 Stage 2) The answer to a hosting-schedule question, if any.
        schedule: schedulePlan,
        // (30 Sep 2026, H17) Host for the map/Directions, if one was chosen
        hostMemberId: newEvent.hostMemberId || undefined
      })
    });

    if (!response.ok) {
      const error = await response.json();
      if (error.code && TIER_LIMIT_ERROR_CODES.includes(error.code)) {
        setTierError({ code: error.code, message: error.message });
        setIsAddingEvent(false);
        return null;
      }
      // (6 Oct 2026, H18 Stage 2) The server needs an answer about the hosting
      // schedule first: the form stays open and the question is shown.
      if (error.code === 'SCHEDULE_CHOICE') {
        setScheduleChoice({ source: 'event', ...error });
        setIsAddingEvent(false);
        return null;
      }
      throw new Error(error.error || 'Failed to create event');
    }

    // Capture the new event's event_uid from the POST response so we can stamp it
    const createdData = await response.json();
    const newEventUid = createdData.id || null;
    // (5 Oct 2026, H18 Stage 1) The server may write the Location itself ("{host}'s" or
    // "Host wanted"), so the WhatsApp message uses what was saved.
    let savedLocation = newEvent.location;

    // Save per-event timezone if the user set a custom one
    if (eventUseCustomTz && newEventUid) {
      await handleSaveEventTimezone(
        newEventUid,
        eventStartTz || calendarTimezone,
        eventDiffEndTz ? (eventEndTz || eventStartTz || calendarTimezone) : null
      );
    }

    // Reload events from API to stay in sync with database
    const refreshResponse = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/events`);
      if (refreshResponse.ok) {
        const eventsData = await refreshResponse.json();
        const transformedEvents = eventsData.map(evt => ({
          id: evt.event_uid,
          title: evt.title,
          description: evt.description,
          location: evt.location,
          date: evt.event_date,
          time: evt.start_time,
          endTime: evt.end_time,
          created: evt.created_at,
          eventUid: evt.event_uid,
          start_timezone: evt.start_timezone || null,
          end_timezone: evt.end_timezone || null,
          hostMemberId: evt.host_member_id || null, // (30 Sep 2026, H17)
          historyIndexValue: evt.history_index_value || null, // (9 Oct 2026, 8.19/8.20, decision 65)
          hostLocation: evt.hostLocation || null,   // (30 Sep 2026, H17)
          poll: evt.poll || null,
          hasPollHistory: evt.hasPollHistory || false,
          pollOpensOn: evt.pollOpensOn || null, // (23 Oct 2026, 8.43, decision 112)
          pollSuppressed: evt.poll_suppressed === true, // (24 Oct 2026, decision 131)
          eventAdmin: evt.eventAdmin || null, // (26 Oct 2026, decision 145)
          waLog: []
        }));
        try {
          const logRes = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/whatsapp-log`);
          if (logRes.ok) {
            const logData = await logRes.json();
            transformedEvents.forEach(e => {
              e.waLog = logData.filter(l => l.event_uid === e.id).sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
            });
          }
        } catch (_) {}
        setEventsChanged(transformedEvents);
        const saved = eventsData.find(evt => evt.event_uid === newEventUid);
        if (saved) savedLocation = saved.location || '';
    }

      setHostBoardVersion(v => v + 1); // the board month (if any) is now linked to the new event

      const nextTime = getNextQuarterHour();
    setNewEvent({ title: '', description: '', location: '', date: new Date().toISOString().split('T')[0], time: nextTime, endTime: getEndTime(nextTime) });
    setEventUseCustomTz(false); setEventDiffEndTz(false); setEventStartTz(''); setEventEndTz('');

    setShowAddEvent(false); // Close the form first

    // Ask owner whether to send a WhatsApp notification
    const addedTitle = newEvent.title;
    const dateStr = newEvent.date
      ? new Date(newEvent.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
      : '';
    const timePart = newEvent.time ? `\n\n🕐 ${newEvent.time}${newEvent.endTime ? '–' + newEvent.endTime : ''}` : '';
    const locationPart = savedLocation ? `\n\n📍 ${savedLocation}` : '';
    const descPart = newEvent.description ? `\n\nNotes: ${newEvent.description}` : '';
    const details = `📅 ${dateStr}${timePart}${locationPart}${descPart}`;

    const addModal = {
      type: 'event_add',
      subject: addedTitle,
      changes: details,
      calendarName: currentCalendar.name,
      shortCode: currentCalendar.short_code,
      eventId: newEventUid,
      calendarId: null,
      isFirstSend: true
    };
    if (!(plan && plan.deferNotify)) setWhatsappNotifyModal(addModal);

    // (6 Oct 2026, H18 Stage 2) One message: what happened on the schedule and any
    // warning now travel with "Event added" (a second toast used to replace the first).
    if (createdData.boardLinkFailed) {
      showStatus('Event added, but the hosting schedule could not be updated. Please check that month on the schedule.', 'error', 7000);
    } else {
      // (7 Oct 2026, decision 11) A one-off's note is the whole message ("Event added as a
      // one-off in August 2027: ..."), so it replaces "Event added." rather than following it.
      if (createdData.scheduleNoteWhole && createdData.scheduleNote) {
        const whole = [createdData.scheduleNote, createdData.warning].filter(Boolean).join(' ');
        showStatus(whole, 'success', 'long');
      } else {
        const extra = [createdData.scheduleNote, createdData.warning].filter(Boolean).join(' ');
        showStatus(extra ? `Event added. ${extra}` : 'Event added successfully!', 'success', extra ? 'long' : undefined);
      }
    }
    return addModal;

  } catch (error) {
    console.error('Error adding event:', error);
    showStatus(error.message, 'error');
    return null;
  } finally {
    setIsAddingEvent(false);
  }
};

// (6 Oct 2026, H18 Stage 2) "Add it to the hosting schedule only for now": the form's
// date, host and notes become a schedule row ("Day proposed"), with no event or poll
// yet. The title, times and location are not kept: the event is created later from the
// row ("Create event"). plan: { mode: 'second' | 'reopen', slotId } after a question.
const addToScheduleOnly = async (plan) => {
  if (!newEvent.date) {
    showStatus('Please choose the date', 'error');
    return null;
  }
  setIsAddingEvent(true);
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/host-board/rows`, {
      method: 'POST',
      body: JSON.stringify({
        date: newEvent.date,
        hostMemberId: newEvent.hostMemberId || undefined,
        note: withoutBareKeyword(currentCalendar, newEvent.description) || undefined, // (decision 66) no bare keyword
        mode: plan ? plan.mode : undefined,
        slotId: plan ? plan.slotId : undefined
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (data.code === 'SCHEDULE_CHOICE') {
        setScheduleChoice({ source: 'scheduleOnly', ...data });
        return null;
      }
      showStatus(data.error || 'Could not add the date to the hosting schedule', 'error');
      return null;
    }
    const nextTime = getNextQuarterHour();
    setNewEvent({ title: '', description: '', location: '', date: new Date().toISOString().split('T')[0], time: nextTime, endTime: getEndTime(nextTime) });
    setEventUseCustomTz(false); setEventDiffEndTz(false); setEventStartTz(''); setEventEndTz('');
    setShowAddEvent(false);
    setHostBoardVersion(v => v + 1);
    const done = `Added to the hosting schedule as ${data.label || 'that month'} (day proposed). Create the event from its row when you're ready.`;
    showStatus(data.warning ? `${done} ${data.warning}` : done, 'success', 'long');
    return null;
  } catch (error) {
    console.error('Error adding the date to the hosting schedule:', error);
    showStatus('Could not add the date to the hosting schedule', 'error');
    return null;
  } finally {
    setIsAddingEvent(false);
  }
};

// (6 Oct 2026, H18 Stage 2) The administrator's answer to a hosting-schedule question.
//   'new_row' / 'second' / 'one_off' / 'reopen': the event (or schedule-only date) is
//     sent again with that answer.
//   'instead': the existing event of that month is deleted (as Delete Event does, with
//     its own WhatsApp prompt), then the new event takes its row; the two prompts are
//     shown one after the other.
// (7 Oct 2026, decision 6) pick: { slotId, existingEvent } of the meeting chosen with
// "Instead of {day}" when the month has more than one; otherwise the first meeting.
const resolveScheduleChoice = async (mode, pick) => {
  const choice = scheduleChoice;
  setScheduleChoice(null);
  if (!choice || !mode) return;
  // (11 Oct 2026, Stage 3, decision 93) Edit Event moving an event to another month:
  // the same questions as Add Event. "Replaces the … event" deletes that event first.
  if (choice.source === 'edit') {
    if (mode === 'instead') {
      const target = pick && pick.slotId ? pick : { slotId: choice.slotId, existingEvent: choice.existingEvent };
      const old = target.existingEvent;
      if (!old || !old.uid) {
        showStatus('Could not find the existing event. Please refresh and try again.', 'error');
        return;
      }
      const deleteModal = await deleteEvent({ eventId: old.uid, monthOutcome: 'replace', deferNotify: true });
      if (deleteModal === false) return;
      await saveEditEvent({ schedule: { mode: 'link', slotId: target.slotId } }, { firstModal: deleteModal || null });
      return;
    }
    await saveEditEvent({ schedule: { mode, slotId: choice.slotId } });
    return;
  }
  if (choice.source === 'scheduleOnly') {
    await addToScheduleOnly({ mode, slotId: choice.slotId });
    return;
  }
  if (mode === 'instead') {
    const target = pick && pick.slotId ? pick : { slotId: choice.slotId, existingEvent: choice.existingEvent };
    const old = target.existingEvent;
    if (!old || !old.uid) {
      showStatus('Could not find the existing event. Please refresh and try again.', 'error');
      return;
    }
    const deleteModal = await deleteEvent({ eventId: old.uid, monthOutcome: 'replace', deferNotify: true });
    if (deleteModal === false) return; // the delete failed and has said so
    const addModal = await addEvent({ mode: 'link', slotId: target.slotId, deferNotify: true });
    if (deleteModal) {
      // (7 Oct 2026, decision 24) Two prompts in a row: number them "(1 of 2)", "(2 of 2)".
      if (addModal) {
        pendingNotifyRef.current.push({ ...addModal, sequence: { index: 2, total: 2 } });
        setWhatsappNotifyModal({ ...deleteModal, sequence: { index: 1, total: 2 } });
      } else {
        setWhatsappNotifyModal(deleteModal);
      }
    } else if (addModal) {
      setWhatsappNotifyModal(addModal);
    }
    return;
  }
  await addEvent({ mode, slotId: choice.slotId });
};

const confirmDeleteEvent = (eventId) => {
  setEventToDelete(eventId);
  setShowDeleteEventConfirm(true);
};

// (5 Oct 2026, H18 Stage 1) monthOutcome: for an event on a hosting-schedule month,
// 'off' (the meeting is off: the month shows "No meeting", with an optional note) or
// 'redate' (the month goes back to "Day to be confirmed"). Ignored for other events.
// (6 Oct 2026, H18 Stage 2) Also: eventId (delete this event without the confirmation
// dialog: used by the schedule editor's "Is that meeting off too? Yes" and by "instead
// of it"), monthOutcome 'replace' (the month keeps its row for the event replacing this
// one), and deferNotify (return the WhatsApp prompt instead of showing it). Returns
// false if the delete failed, else the prompt (or null when there is none).
const deleteEvent = async ({ monthOutcome, monthNote, eventId: eventIdArg, deferNotify, historyChoice } = {}) => {
  const eventId = eventIdArg || eventToDelete;
  if (!eventId) return false;
  // Capture event details before we remove it from state
  const deletedEvent = events.find(e => e.id === eventId || e.eventUid === eventId);
  setShowDeleteEventConfirm(false);
  setEventToDelete(null);
    
  try {
    const monthQueryRaw = monthOutcome === 'off'
      ? `?month=off${monthNote && monthNote.trim() ? `&note=${encodeURIComponent(monthNote.trim())}` : ''}`
      : (monthOutcome === 'replace' ? '?month=replace' : '');
    // (9 Oct 2026, decision 62) A past meeting in the calendar history:
    // keep it there (the default) or delete it from the history too.
    const monthQuery = historyChoice === 'delete'
      ? `${monthQueryRaw}${monthQueryRaw ? '&' : '?'}history=delete`
      : monthQueryRaw;
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/calendars/${currentCalendar.id}/events/${eventId}${monthQuery}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete event');
    }

    const updatedEvents = events.filter(e => e.id !== eventId && e.eventUid !== eventId);
    setEventsChanged(updatedEvents);
    setHostBoardVersion(v => v + 1); // a linked board month has just been reset
    // (29 Sep 2026, H12) Deleting an event removes its bell notices on the
    // server; refresh the bell now so none are left on screen to go stale.
    fetchPendingNotifications();
    
    const updatedCalendar = { 
      ...currentCalendar, 
      events: updatedEvents 
    };
    await window.storage.set('calendar:' + currentCalendar.id, JSON.stringify(updatedCalendar));

    // Ask owner whether to send a WhatsApp notification
    let deleteModal = null;
    if (deletedEvent) {
      const dateStr = deletedEvent.date
        ? new Date(deletedEvent.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
        : '';
      const timePart = deletedEvent.time ? `\n\n🕐 ${formatTime(deletedEvent.time)}${deletedEvent.endTime ? '–' + formatTime(deletedEvent.endTime) : ''}` : '';
      const descPartDel = deletedEvent.description ? `\n\nNotes: ${deletedEvent.description}` : '';
      const details = `📅 ${dateStr}${timePart}${deletedEvent.location ? `\n\n📍 ${deletedEvent.location}` : ''}${descPartDel}`;
      deleteModal = {
        type: 'event_delete',
        subject: deletedEvent.title,
        changes: details,
        calendarName: currentCalendar.name,
        shortCode: currentCalendar.short_code,
        eventId: null, // event is deleted — no point stamping it
        calendarId: null,
        // (30 Sep 2026, H15) The event no longer exists on the server, so its
        // details travel with the prompt for the "event cancelled" emails.
        deletedEvent: {
          title: deletedEvent.title,
          date: deletedEvent.date,
          time: deletedEvent.time,
          endTime: deletedEvent.endTime,
          location: deletedEvent.location,
          description: deletedEvent.description,
        }
      };
      if (!deferNotify) setWhatsappNotifyModal(deleteModal);
    }

    showStatus('Event deleted successfully!', 'success');
    return deleteModal;
  } catch (error) {
    console.error('Error deleting event:', error);
    showStatus('Error deleting event: ' + error.message, 'error');
    return false;
  }
};

  const startEditEvent = (event) => {
    setEditingEvent(event);
    setNewEvent({
      title: event.title,
      description: event.description || '',
      location: event.location || '',
      date: event.date,
      time: formatTime(event.time) || getNextQuarterHour(),
      endTime: formatTime(event.endTime) || getEndTime(event.time || getNextQuarterHour()),
      hostMemberId: event.hostMemberId || '', // (30 Sep 2026, H17)
      historyIndexValue: event.historyIndexValue || '' // (9 Oct 2026, decision 65)
    });
    // Pre-populate timezone UI if the event has overrides stored
    const hasTzOverride = !!(event.start_timezone);
    setEventUseCustomTz(hasTzOverride);
    setEventStartTz(event.start_timezone || '');
    setEventDiffEndTz(!!(event.end_timezone && event.end_timezone !== event.start_timezone));
    setEventEndTz(event.end_timezone || '');
    setShowEditEvent(true);
    setTimeout(() => {
      if (editEventRef.current) {
        const yOffset = -180; // clear the dashboard header
        const y = editEventRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 50);
  };

  // (11 Oct 2026, Stage 3) answers: replies to the server's questions about a date
  // change ({ schedule, moveHistory, pollPlan }), kept in editAnswersRef until the
  // save goes through. firstModal: a prompt to show before this edit's own (the
  // deletion of the event this one replaces on the hosting schedule).
  const saveEditEvent = async (answers, { firstModal = null } = {}) => {
    if (!answers || typeof answers !== 'object' || answers.nativeEvent || answers.target) {
      editAnswersRef.current = {}; // pressed from the form: a fresh save
    } else {
      editAnswersRef.current = { ...editAnswersRef.current, ...answers };
    }
    if (!newEvent.title || !newEvent.date || !editingEvent) {
      showStatus('Please enter event title and date', 'error');
      return;
    }
    // (9 Oct 2026, 8.19/8.20, decisions 50, 65) An event in the history needs its index value.
    const notesToSaveEdit = withoutBareKeyword(currentCalendar, newEvent.description);
    const indexMissingEdit = missingIndexMessage(currentCalendar, notesToSaveEdit, newEvent.historyIndexValue);
    if (indexMissingEdit) {
      showStatus(indexMissingEdit, 'error', 6000);
      return;
    }

    setIsAddingEvent(true);

    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/api/calendars/${currentCalendar.id}/events/${editingEvent.id}`, 
        {
          method: 'PATCH',
          body: JSON.stringify({
            title: newEvent.title,
            description: notesToSaveEdit,
            location: newEvent.location,
            date: newEvent.date,
            time: newEvent.time,
            endTime: newEvent.endTime,
            // (30 Sep 2026, H17) '' clears the host; the form always sends it.
            hostMemberId: newEvent.hostMemberId || '',
            historyIndexValue: (newEvent.historyIndexValue || '').trim(), // (9 Oct 2026, decision 65)
            ...editAnswersRef.current // (11 Oct 2026, Stage 3)
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        if (error.code && TIER_LIMIT_ERROR_CODES.includes(error.code)) {
          setTierError({ code: error.code, message: error.message });
          setIsAddingEvent(false);
          return;
        }
        // (11 Oct 2026, Stage 3) The date change needs an answer first; the form stays
        // open and nothing has been saved.
        if (error.code === 'SCHEDULE_CHOICE') {
          setScheduleChoice({ source: 'edit', ...error });
          setIsAddingEvent(false);
          return;
        }
        if (error.code === 'HISTORY_MOVE') {
          setEditQuestion({ type: 'history', name: error.name, oldDate: error.oldDate, newDate: error.newDate });
          setIsAddingEvent(false);
          return;
        }
        if (error.code === 'POLL_DATES' && error.timetable) {
          setEditQuestion({ type: 'poll', timetable: error.timetable });
          setIsAddingEvent(false);
          return;
        }
        throw new Error(error.error || 'Failed to update event');
      }
      editAnswersRef.current = {};

      // (5 Oct 2026, H18 Stage 1) The server may rewrite the Location ("{host}'s" /
      // "Host wanted") and may say the schedule's host changed too.
      const updatedData = await response.json().catch(() => ({}));
      let savedLocation = newEvent.location;
      if (updatedData && updatedData.event) savedLocation = updatedData.event.location || '';
      // (11 Oct 2026, Stage 3) Shown with "Event updated" at the end (it used to be
      // replaced at once by that message).
      const extraNote = [updatedData && updatedData.note, updatedData && updatedData.warning].filter(Boolean).join(' ');
      const reconfirm = !!(updatedData && updatedData.pollOutcome === 'reconfirm');

      // Reload events from API
      const refreshResponse = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/events`);
      if (refreshResponse.ok) {
        const eventsData = await refreshResponse.json();
        const transformedEvents = eventsData.map(evt => ({
          id: evt.event_uid,
          title: evt.title,
          description: evt.description,
          location: evt.location,
          date: evt.event_date,
          time: evt.start_time,
          endTime: evt.end_time,
          created: evt.created_at,
          eventUid: evt.event_uid,
          start_timezone: evt.start_timezone || null,
          end_timezone: evt.end_timezone || null,
          hostMemberId: evt.host_member_id || null, // (30 Sep 2026, H17)
          historyIndexValue: evt.history_index_value || null, // (9 Oct 2026, 8.19/8.20, decision 65)
          hostLocation: evt.hostLocation || null,   // (30 Sep 2026, H17)
          poll: evt.poll || null,
          hasPollHistory: evt.hasPollHistory || false,
          pollOpensOn: evt.pollOpensOn || null, // (23 Oct 2026, 8.43, decision 112)
          pollSuppressed: evt.poll_suppressed === true, // (24 Oct 2026, decision 131)
          eventAdmin: evt.eventAdmin || null, // (26 Oct 2026, decision 145)
          waLog: []
        }));
        try {
          const logRes = await authenticatedFetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/whatsapp-log`);
          if (logRes.ok) {
            const logData = await logRes.json();
            transformedEvents.forEach(e => {
              e.waLog = logData.filter(l => l.event_uid === e.id).sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
            });
          }
        } catch (_) {}
        setEventsChanged(transformedEvents);
      }

      setHostBoardVersion(v => v + 1); // a linked board month may have followed a date change

      // Build event_edit notification — always show all parameters, label changed ones
      const oldTime = formatTime(editingEvent.time) || '';
      const newTime = newEvent.time || '';
      const oldEnd = formatTime(editingEvent.endTime) || '';
      const newEnd = newEvent.endTime || '';
      const dateChanged = editingEvent.date !== newEvent.date;
      const timeChanged = oldTime !== newTime || oldEnd !== newEnd;
      const locationChanged = (editingEvent.location || '') !== (savedLocation || '');
      const descChanged = (editingEvent.description || '') !== (newEvent.description || '');
      const titleChanged = editingEvent.title !== newEvent.title;

      const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
      const fmtTimeRange = (t, e) => t + (e ? '–' + e : '');
      const newTimeRange = fmtTimeRange(newTime, newEnd);
      const newDateStr = fmtDate(newEvent.date);

      const editLines = [];

      // Title
      if (titleChanged) editLines.push(`Event title changed`);

      // Date/time lines — label depends on what changed
      if (dateChanged) {
        editLines.push(`📅 New date: ${newDateStr}`);
      } else {
        editLines.push(`📅 ${newDateStr}`);
      }

      if (timeChanged) {
        editLines.push(`🕐 New time: ${newTimeRange}`);
      } else {
        editLines.push(`🕐 ${newTimeRange}`);
      }

      // Location
      if (locationChanged) {
        editLines.push(`📍 Location change: ${savedLocation || '(removed)'}`);
      } else if (savedLocation) {
        editLines.push(`📍 ${savedLocation}`);
      }

      // Description — show current value if changed
      if (descChanged && newEvent.description) {
        editLines.push(`New notes: ${newEvent.description}`);
      }

      const changesText = editLines.join('\n\n');

      const editedEventId = editingEvent.id;

      // Save per-event timezone override if set
      if (eventUseCustomTz && editingEvent?.id) {
        await handleSaveEventTimezone(
          editingEvent.id,
          eventStartTz || calendarTimezone,
          eventDiffEndTz ? (eventEndTz || eventStartTz || calendarTimezone) : null
        );
      }

      const nextTime = getNextQuarterHour();
      setNewEvent({ title: '', description: '', location: '', date: new Date().toISOString().split('T')[0], time: nextTime, endTime: getEndTime(nextTime) });
      setEventUseCustomTz(false); setEventDiffEndTz(false); setEventStartTz(''); setEventEndTz('');

      setShowEditEvent(false);
      setEditingEvent(null);

      // Ask owner whether to send a WhatsApp notification
      const editedEvt = events.find(e => e.id === editedEventId);
      const editModal = {
        type: 'event_edit',
        subject: newEvent.title,
        changes: changesText,
        calendarName: currentCalendar.name,
        shortCode: currentCalendar.short_code,
        eventId: editedEventId,
        calendarId: null,
        isFirstSend: !editedEvt || !editedEvt.waLog || editedEvt.waLog.length === 0,
        // (11 Oct 2026, Stage 3, decision 91) Members were asked to confirm again.
        reconfirm,
        // (21 Oct 2026, 8.42, decision 107) What moved, for the WhatsApp text and the emails.
        previousDate: dateChanged ? String(editingEvent.date).slice(0, 10) : null,
        newDate: String(newEvent.date).slice(0, 10)
      };
      if (firstModal) {
        // (decision 24) Two prompts in a row: the deletion first, then this change.
        pendingNotifyRef.current.push({ ...editModal, sequence: { index: 2, total: 2 } });
        setWhatsappNotifyModal({ ...firstModal, sequence: { index: 1, total: 2 } });
      } else {
        setWhatsappNotifyModal(editModal);
      }

      if (extraNote) showStatus(`Event updated. ${extraNote}`, updatedData.warning ? 'info' : 'success', 'long');
      else showStatus('Event updated successfully!', 'success');

    } catch (error) {
      console.error('Error updating event:', error);
      showStatus(error.message, 'error');
    } finally {
      setIsAddingEvent(false);
    }
  };

  const handleSaveEventTimezone = async (eventId, startTzid, endTzid) => {
  if (!currentCalendar || !eventId) return;
  try {
    const body = { start_timezone: startTzid || null };
    if (endTzid && endTzid !== startTzid) body.end_timezone = endTzid;
    else body.end_timezone = null;

    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/calendars/${currentCalendar.id}/events/${eventId}/timezone`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }
    );
    if (!response.ok) throw new Error('Failed to save event timezone');
  } catch (err) {
    console.error('Error saving event timezone:', err);
    // Non-fatal — don't block main event save flow
  }
};

const cancelEdit = () => {
    setShowEditEvent(false);
    setEditingEvent(null);
    const nextTime = getNextQuarterHour();
    setNewEvent({ title: '', description: '', location: '', date: new Date().toISOString().split('T')[0], time: nextTime, endTime: getEndTime(nextTime) });
    setEventUseCustomTz(false); setEventDiffEndTz(false); setEventStartTz(''); setEventEndTz('');
  };

  const shareToWhatsApp = (event) => {
    const dateStr = event.date
      ? new Date(event.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
      : '';
    const timePart = event.time ? `\n\n🕐 ${formatTime(event.time)}${event.endTime ? '-' + formatTime(event.endTime) : ''}` : '';
    const locationPart = event.location ? `\n\n📍 ${event.location}` : '';
    const descPart = event.description ? `\n\nNotes: ${event.description}` : '';
    const changes = `📅 ${dateStr}${timePart}${locationPart}${descPart}`;
    const isFirstSend = !event.waLog || event.waLog.length === 0;
    const message = buildWaNotifyMessage({
      type: 'event_add',
      subject: event.title,
      changes,
      calendarName: currentCalendar.name,
      isFirstSend
    });
    window.open('https://wa.me/?text=' + encodeURIComponent(message), '_blank');
    // Log this share (non-fatal)
    const action = isFirstSend ? 'created' : 'resend';
    authenticatedFetch(
      `${API_BASE_URL}/api/events/${event.id}/whatsapp-notified`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }) }
    ).then(() => {
      const newEntry = { event_uid: event.id, action, sent_at: new Date().toISOString() };
      setEvents(prev => prev.map(e =>
        e.id === event.id
          ? { ...e, waLog: [...(e.waLog || []), newEntry] }
          : e
      ));
    }).catch(() => {});
  };

  // (8 Oct 2026, 8.14 T2) shareCalendar, the tooltip flag and the shared subscription
  // URLs were passed to DashboardStep but never used there; removed.

  // ── WhatsApp notification helpers ────────────────────────────────────────

  // Build the pre-drafted message shown in the notify prompt
  const buildWaNotifyMessage = (modal) => {
    const { type, subject, changes, calendarName } = modal;

    // (20 Sep 2026) Host schedule board updated: a short group announcement, not a
    // calendar-sync notice, so it skips the event footer below.
    if (type === 'host_board_update') {
      return `The ${calendarName} hosting schedule has been updated for your review.\n\nhttps://calendar.telematrixconsulting.com`;
    }

    // (13 Sep 2026) Poll phase-open messages are self-contained group
    // announcements, not calendar-sync notices — the shared
    // subjectLine/footer plumbing below is built around "an event
    // changed, here's how to subscribe," which doesn't fit "a poll just
    // opened." Composed and returned directly, matching the wording
    // pollScheduler.js's queueGaNotification already uses for the
    // scheduler-triggered case, so both paths read the same way.
    if (type === 'poll_phase1_open' || type === 'poll_phase2_open' || type === 'poll_results_ready') {
      // (23 Sep 2026) Wording agreed with Neil — must match pollScheduler.js's
      // messageByReason word for word: *event name* (bold in WhatsApp), date
      // in the GA's preferred format, link on its own line.
      // (29 Sep 2026, H13) Now "Poll phase 1 (offers)…", no calendar name (the
      // group is the calendar's own; the name doubled when a title began
      // with it), and "what's being offered".
      const dateStr = modal.eventDate ? formatDateByPref(modal.eventDate, userTier?.preferred_date_format) : '';
      const what = modal.eventTitle
        ? `the *${modal.eventTitle}* event${dateStr ? ' on ' + dateStr : ''}`
        : `the event${dateStr ? ' on ' + dateStr : ''}`;
      const link = '\nhttps://calendar.telematrixconsulting.com';
      // (21 Oct 2026, 8.42, decisions 104, 113, 115) Both openings say when the phase
      // closes (modal.closesOn, YYYY-MM-DD, from pollRoutes.js); "any dish you offered".
      const closesText = fullDayDate(modal.closesOn);
      const closing = closesText ? ` It closes at 7 a.m. on ${closesText}.` : '';
      if (type === 'poll_phase1_open') {
        return `Poll phase 1 (offers) for ${what} is now open — you can indicate your attendance and offer your dish.${closing}${link}`;
      }
      if (type === 'poll_phase2_open') {
        return `Poll phase 2 (confirm) for ${what} is now open — please give your final confirmation of attendance, and of any dish you offered.${closing}${link}`;
      }
      // (8 Oct 2026, decision 3) Must match pollScheduler.js's phase2_closed.
      return `Poll phase 2 (confirm) for ${what} is now closed and the poll cycle is therefore complete — sign in to see who's coming and what's being offered.${link}`;
    }

    let title = '';
    let subjectLine = '';
    switch (type) {
      case 'event_add':
        title = `An event has been added to the ${calendarName} calendar.`;
        subjectLine = `*Event: ${subject}*`;
        break;
      case 'event_edit':
        title = `An event on the ${calendarName} calendar has been updated.`;
        subjectLine = `*Event: ${subject}*`;
        break;
      case 'event_delete':
        title = `An event on the ${calendarName} calendar has been deleted.`;
        subjectLine = `*Event: ${subject}*`;
        break;
      case 'calendar_rename':
        title = `The ${calendarName} calendar has been renamed.`;
        subjectLine = '';
        break;
      default: break;
    }

    let msg = subjectLine ? `${title}\n\n${subjectLine}` : title;
    if (changes) msg += `\n\n${changes}`;
    // (21 Oct 2026, 8.42, decision 107) When the date changed, say what moved (same words
    // as the event-change email); the reconfirm sentence, where it applies, follows.
    if (type === 'event_edit' && modal.previousDate && modal.newDate && modal.previousDate !== modal.newDate) {
      const which = subject ? `The *${subject}* event` : 'The event';
      msg += `\n\n${which} has moved from ${fullDayDate(modal.previousDate)} to ${fullDayDate(modal.newDate)}. The details above are as they now stand.`;
    }
    // (11 Oct 2026, Stage 3, decision 91) Same sentence as emailService.js RECONFIRM_SENTENCE.
    if (type === 'event_edit' && modal.reconfirm) {
      msg += "\n\nA date change was necessary, which means more of you may now be able to attend. Would all members please reconfirm your attendance and, if you're coming, the dish you're offering.";
    }
    // (5 Oct 2026, 8.8) The long subscription footer is replaced by one line
    // pointing to the help page (HelpPage.jsx). The help link comes after the
    // main Calendar Hub link so WhatsApp's preview card still shows Calendar Hub.
    msg += '\n\nhttps://calendar.telematrixconsulting.com';
    msg += '\n\nEvents not appearing in your calendar? See https://calendar.telematrixconsulting.com/?help=subscribe';
    return msg;
  };

  // Called when owner taps "Send" on the notify prompt
  // (7 Oct 2026, decision 19) keepOpen: the prompt stays open after WhatsApp is opened,
  // so the administrator can also email members, then press Done.
  const handleSendWaNotification = async (modal, { keepOpen = false } = {}) => {
    const message = buildWaNotifyMessage(modal);
    window.open('https://wa.me/?text=' + encodeURIComponent(message), '_blank');

    // (27 Sep 2026, A6) A manual poll open/close also queued a bell notice;
    // sending from this prompt clears it (Skip here leaves it in the bell,
    // where the button is called Dismiss — 29 Sep 2026, H12).
    if (modal.gaNotificationId) {
      try {
        await authenticatedFetch(`${API_BASE_URL}/api/notifications/${modal.gaNotificationId}/resolve`, {
          method: 'POST',
          body: JSON.stringify({ action: 'sent' })
        });
      } catch (_) {}
      fetchPendingNotifications();
    }

    // Record log entry (non-fatal — don't block UX if it fails)
    try {
      if (modal.type === 'host_board_update' && modal.boardCalendarId) {
        // Sent (not skipped): reset the board's "changes since members were last told" count.
        await authenticatedFetch(
          `${API_BASE_URL}/api/calendars/${modal.boardCalendarId}/host-board/announced`,
          { method: 'POST' }
        );
        setHostBoardVersion(v => v + 1);
      } else if (modal.eventId) {
        const action = modal.isFirstSend ? 'created' : 'updated';
        await authenticatedFetch(
          `${API_BASE_URL}/api/events/${modal.eventId}/whatsapp-notified`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }) }
        );
        // Update local waLog state so card renders immediately
        const newEntry = { event_uid: modal.eventId, action, sent_at: new Date().toISOString() };
        setEvents(prev => prev.map(e =>
          e.id === modal.eventId
            ? { ...e, waLog: [...(e.waLog || []), newEntry] }
            : e
        ));
      } else if (modal.calendarId) {
        await authenticatedFetch(
          `${API_BASE_URL}/api/calendars/${modal.calendarId}/whatsapp-notified`,
          { method: 'POST' }
        );
      }
    } catch (_) {}

    if (!keepOpen) setWhatsappNotifyModal(null);
  };

  // (13 Sep 2026) Scheduler-queued poll notifications (Bell icon panel) —
  // the message is already composed server-side by pollScheduler.js's
  // queueGaNotification. "Send via WhatsApp" opens it and marks the
  // notification resolved in one action, same one-click shape as
  // everywhere else WhatsApp sharing happens in this app — no separate
  // "did you actually send it" confirmation step.
  const handleSendPollNotification = (notification) => {
    // (23 Sep 2026) The stored text keeps ISO dates (the bell reformats them
    // on display); reformat here too, so the group sees e.g. 5-Oct-2026.
    const text = (notification.message_text || '').replace(/\d{4}-\d{2}-\d{2}/g, (d) => formatDateByPref(d, userTier?.preferred_date_format));
    window.open('https://wa.me/?text=' + encodeURIComponent(text), '_blank');
    resolveNotification(notification.id, 'sent');
  };



  const sortedEvents = [...events].sort((a, b) => new Date(a.date) - new Date(b.date));

  // (5 Oct 2026) The help page (?help=subscribe, linked from every WhatsApp
  // event message) is shown before anything else, signed in or not.
  if (new URLSearchParams(window.location.search).get('help') === 'subscribe') {
    return <HelpPage />;
  }

  // Suppress render until initial auth/calendar check completes — prevents welcome screen flash
  if (isAppLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="bg-blue-100 p-4 rounded-full">
          <Calendar className="w-10 h-10 text-blue-600 animate-pulse" />
        </div>
      </div>
    );
  }

  if (step === 'needs-profile-completion') {
    // (22 Sep 2026) Profile pinned open, every other tab visible but
    // disabled (see SettingsModal.jsx's requireProfileCompletion), no
    // Close/Cancel/X — the person cannot leave until First Name, Last
    // Name and (for anyone but a secondary contact) Preferred Name are
    // filled in and saved. Rendered as its own full page here (there's no
    // WelcomeStep/DashboardStep behind it yet at this point in sign-in),
    // so the toast that onStatusMessage normally relies on a parent step
    // to render is drawn locally instead.
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <StatusToast statusMessage={statusMessage} placement="bottom" />
        <SettingsModal
          isOpen={true}
          onClose={() => {}}
          userEmail={userEmail}
          apiBaseUrl={API_BASE_URL}
          authToken={localStorage.getItem('auth_token')}
          onStatusMessage={showStatus}
          userTier={userTier}
          firstName={firstName}
          lastName={lastName}
          onSaveProfile={(fn, ln, pn) => { setFirstName(fn); setLastName(ln); setPreferredName(pn); }}
          requireProfileCompletion={true}
          secondaryOnly={isSecondaryOnly}
          // (25 Sep 2026) The person's role isn't known yet at this point in
          // sign-in, so show only the tabs every member has (Profile and
          // Preferences), locked, rather than the full administrator list.
          // An administrator's full set appears after the reload below.
          restrictToProfileTabs={true}
          onProfileCompleted={() => window.location.reload()}
        />
      </div>
    );
  }

  if (step === 'no-access') {
    // (22 Sep 2026, Neil's decision) Genuinely no authority, no
    // membership, no calendar-creation right anywhere — replaces the old
    // OnboardingStep "Choose a Plan" self-serve signup screen for this
    // case, which predates the current BETA_ALLOWLIST/invite-only model
    // and led nowhere real in it. Same wording as the sign-in gate's
    // rejection (authRoutes.js's request-magic-link) for consistency —
    // deliberately terse, no website or contact email, matching Neil's
    // choice there not to be more helpful to someone just trying their
    // luck. English-only, unlike OnboardingStep's EN/FR toggle — a
    // one-line rejection message for an edge case doesn't carry the same
    // bilingual-support weight as the paid-signup flow.
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        {/* (8 Oct 2026, 8.35) Shows decision 30's message when arriving from a removal. */}
        <StatusToast statusMessage={statusMessage} />
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-sm w-full text-center">
          {/* (8 Oct 2026, decision 31) Signed in, but no calendars left. */}
          <h2 className="text-xl font-bold text-gray-800 mb-3">No calendars</h2>
          <p className="text-sm text-gray-600 mb-6">
            You're not a member of any calendar on Calendar Hub at the moment. If you think this is a mistake, please contact the administrator who added you.
          </p>
          <button
            onClick={() => { clearAuthToken(); window.location.reload(); }}
            className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (step === 'welcome') {
    return (
      <WelcomeStep
        statusMessage={statusMessage}
        // (11 Oct 2026, 8.4) A tab that sent a sign-in email stays on the sign-in
        // box, even once another tab has stored the shared sign-in: otherwise its
        // next redraw showed the signed-in welcome page instead of "Email sent!" or
        // "You're signed in in another tab".
        hasAuthToken={!!getAuthToken() && !magicLinkSent}
        magicLinkSent={magicLinkSent}
        setMagicLinkSent={setMagicLinkSent}
        signedInElsewhere={signedInElsewhere}
        loginEmail={loginEmail}
        setLoginEmail={setLoginEmail}
        savedEmails={savedEmails}
        onRemoveSavedEmail={(email) => {
          // (26 Sep 2026) Forget one address on this device.
          const next = savedEmails.filter((e) => e !== email);
          try { localStorage.setItem('saved_emails', JSON.stringify(next)); } catch (_) {}
          if (next.length === 0) { try { localStorage.removeItem('last_login_email'); } catch (_) {} }
          setSavedEmails(next);
          if ((loginEmail || '').trim().toLowerCase() === email) setLoginEmail('');
        }}
        onSendMagicLink={handleSendMagicLink}
        isSendingMagicLink={isSendingMagicLink}
        showUserGuide={showUserGuide}
        onOpenUserGuide={() => setShowUserGuide(true)}
        onCloseUserGuide={() => setShowUserGuide(false)}
        isAuthenticated={isAuthenticated()}
        onSignOut={() => { clearAuthToken(); window.location.reload(); }}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        settingsInitialTab={settingsInitialTab}
        setSettingsInitialTab={setSettingsInitialTab}
        userEmail={userEmail}
        apiBaseUrl={API_BASE_URL}
        authToken={localStorage.getItem('auth_token')}
        onStatusMessage={showStatus}
        userTier={userTier}
        firstName={firstName}
        lastName={lastName}
        preferredName={preferredName}
        onSaveProfile={(fn, ln, pn) => { setFirstName(fn); setLastName(ln); setPreferredName(pn); }}
        isSA={isSA}
        raRegions={raRegions}
      />
    );
  }

  if (step === 'onboarding') {
    return (
      <OnboardingStep
        onboardingLang={onboardingLang}
        setOnboardingLang={setOnboardingLang}
        onboardingCurrency={onboardingCurrency}
        setOnboardingCurrency={setOnboardingCurrency}
        currencyManuallySet={currencyManuallySet}
        setCurrencyManuallySet={setCurrencyManuallySet}
        onboardingTiersList={onboardingTiersList}
        setOnboardingTiersList={setOnboardingTiersList}
        tiersLoading={tiersLoading}
        loadOnboardingTiers={loadOnboardingTiers}
        showPlanSelector={showPlanSelector}
        setShowPlanSelector={setShowPlanSelector}
        activatingTier={activatingTier}
        activatingTierName={activatingTierName}
        handleSelectTier={handleSelectTier}
        expandedTierGroup={expandedTierGroup}
        setExpandedTierGroup={setExpandedTierGroup}
        showComingSoon={showComingSoon}
        setShowComingSoon={setShowComingSoon}
        selectedPaidTier={selectedPaidTier}
        setSelectedPaidTier={setSelectedPaidTier}
        showUserGuide={showUserGuide}
        onOpenUserGuide={() => setShowUserGuide(true)}
        onCloseUserGuide={() => setShowUserGuide(false)}
        onSignOut={() => { clearAuthToken(); window.location.reload(); }}
        userEmail={userEmail}
        userPreferredCurrency={userPreferredCurrency}
      />
    );
  }

  if (step === 'choose-calendar') {
    return (
      <ChooseCalendarStep
        memberCalendars={memberCalendars}
        onSelectCalendar={enterMemberCalendar}
        canCreateCalendar={canCreateCalendar}
        onNewCalendar={() => handleNewCalendarFromMember('choose-calendar')}
        isAuthenticated={isAuthenticated()}
        onSignOut={() => { clearAuthToken(); window.location.reload(); }}
        statusMessage={statusMessage}
        userEmail={userEmail}
        preferredName={preferredName}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        settingsInitialTab={settingsInitialTab}
        setSettingsInitialTab={setSettingsInitialTab}
        apiBaseUrl={API_BASE_URL}
        authToken={localStorage.getItem('auth_token')}
        onStatusMessage={showStatus}
        userTier={userTier}
        firstName={firstName}
        lastName={lastName}
        onSaveProfile={(fn, ln, pn) => { setFirstName(fn); setLastName(ln); setPreferredName(pn); }}
        isSA={isSA}
        raRegions={raRegions}
      />
    );
  }

  if (step === 'poll-page') {
    const userLocalTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return (
      <PollPage
        eventUid={pollPageEventUid}
        apiBaseUrl={API_BASE_URL}
        authToken={localStorage.getItem('auth_token')}
        authenticatedFetch={authenticatedFetch}
        formatShortDate={formatShortDate}
        formatEventDate={(d) => formatEventDateByPref(d, userTier?.preferred_date_format, showWeekday)} // (24 Oct 2026, 8.40a)
        formatTime={formatTime}
        getTzAbbr={getTzAbbr}
        userLocalTimezone={userLocalTimezone}
        onBack={() => {
          const wasProxy = !!pollPageProxy;
          const proxyReturnStep = pollPageProxy?.returnStep || 'main';
          setPollPageProxy(null);
          // (12 Sep 2026) Was a plain proxy/non-proxy binary — always fell
          // through to 'member-calendar' for any non-proxy visit, which
          // silently assumed the only way to reach PollPage without proxy
          // mode was via a plain member's own screen. That assumption
          // broke the same day a second non-proxy entry point (Dashboard's
          // new "Answer as yourself") was added: a GA saving their own
          // answer from the Dashboard got dropped onto member-calendar
          // instead of back onto the Dashboard. Reuses
          // pollResultsReturnStep — already tracking "where did we come
          // from" for PollResultsPage's own Back button — rather than
          // adding a second, parallel piece of state for the same idea.
          const returnStep = wasProxy ? proxyReturnStep : pollResultsReturnStep;
          setStep(returnStep);
          // (23 Sep 2026) Refetch the member's event list on the way back,
          // so the phase pill / replied dot reflect a save just made on
          // the poll page — previously it only refreshed on a full reload.
          if (returnStep === 'member-calendar' && currentMemberCalendar?.id) {
            loadMemberEvents(currentMemberCalendar.id);
          }
        }}
        onStatusMessage={showStatus}
        statusMessage={statusMessage}
        userEmail={userEmail}
        preferredName={preferredName}
        firstName={firstName}
        lastName={lastName}
        isAuthenticated={isAuthenticated()}
        onSignOut={() => { clearAuthToken(); window.location.reload(); }}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        settingsInitialTab={settingsInitialTab}
        setSettingsInitialTab={setSettingsInitialTab}
        onSaveProfile={(fn, ln, pn) => { setFirstName(fn); setLastName(ln); setPreferredName(pn); }}
        userTier={userTier}
        currentMemberCalendar={currentMemberCalendar}
        proxyMode={!!pollPageProxy}
        proxyCalendarId={pollPageProxy?.calendarId}
        proxyMemberId={pollPageProxy?.memberId}
        proxyPollId={pollPageProxy?.pollId}
      />
    );
  }

  if (step === 'poll-results') {
    const userLocalTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return (
      <PollResultsPage
        statusMessage={statusMessage}
        eventUid={pollPageEventUid}
        apiBaseUrl={API_BASE_URL}
        authToken={localStorage.getItem('auth_token')}
        authenticatedFetch={authenticatedFetch}
        formatEventDate={(d) => formatEventDateByPref(d, userTier?.preferred_date_format, showWeekday)} // (24 Oct 2026, 8.40a)
        formatTime={formatTime}
        getTzAbbr={getTzAbbr}
        userLocalTimezone={userLocalTimezone}
        onBack={() => setStep(pollResultsReturnStep)}
        onAnswerForMember={(calendarId, memberId, pollId) =>
          handleAnswerPollForMember({ calendarId, memberId, eventUid: pollPageEventUid, pollId, returnStep: 'poll-results' })}
        onStatusMessage={showStatus}
        userEmail={userEmail}
        preferredName={preferredName}
        firstName={firstName}
        lastName={lastName}
        isAuthenticated={isAuthenticated()}
        onSignOut={() => { clearAuthToken(); window.location.reload(); }}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        settingsInitialTab={settingsInitialTab}
        setSettingsInitialTab={setSettingsInitialTab}
        onSaveProfile={(fn, ln, pn) => { setFirstName(fn); setLastName(ln); setPreferredName(pn); }}
        userTier={userTier}
        currentMemberCalendar={currentMemberCalendar}
      />
    );
  }

  if (step === 'member-calendar') {
    return (
      <MemberCalendarStep
        statusMessage={statusMessage}
        currentMemberCalendar={currentMemberCalendar}
        userEmail={userEmail}
        preferredName={preferredName}
        memberCalendars={memberCalendars}
        onSwitchCalendar={() => setStep('choose-calendar')}
        canCreateCalendar={canCreateCalendar && calendars.length === 0}
        onNewCalendar={() => handleNewCalendarFromMember('member-calendar')}
        adminCalendars={calendars}
        onChooseCalendar={handleChooseCalendar}
        isAuthenticated={isAuthenticated()}
        onSignOut={() => { clearAuthToken(); window.location.reload(); }}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        settingsInitialTab={settingsInitialTab}
        setSettingsInitialTab={setSettingsInitialTab}
        isLoadingMemberEvents={isLoadingMemberEvents}
        memberEvents={memberEvents}
        showingPastEvents={showingPastEvents}
        onTogglePastEvents={handleToggleMemberPastEvents}
        isLoadingMemberPastEvents={isLoadingMemberPastEvents}
        memberPastEvents={memberPastEvents}
        pollHistoryCourses={pollHistoryCourses}
        userTier={userTier}
        showWeekday={showWeekday}
        onOpenPoll={(eventUid, pollStatus) => {
          rememberScroll('member-calendar', eventUid);
          setPollPageEventUid(eventUid);
          setPollResultsReturnStep('member-calendar');
          setStep(pollStatus === 'closed' ? 'poll-results' : 'poll-page');
        }}
        apiBaseUrl={API_BASE_URL}
        authToken={localStorage.getItem('auth_token')}
        onStatusMessage={showStatus}
        firstName={firstName}
        lastName={lastName}
        onSaveProfile={(fn, ln, pn) => { setFirstName(fn); setLastName(ln); setPreferredName(pn); }}
        isSA={isSA}
        raRegions={raRegions}
        hostBoardRefreshToken={memberHostBoardVersion}
        onAccessLost={() => currentMemberCalendar && checkStillMember(currentMemberCalendar.id, currentMemberCalendar.name)}
        // (26 Oct 2026, decision 145) An Event Administrator's controls on their own event.
        onViewResults={(eventUid) => {
          rememberScroll('member-calendar', eventUid);
          setPollPageEventUid(eventUid);
          setPollResultsReturnStep('member-calendar');
          setStep('poll-results');
        }}
        onEventsChanged={() => currentMemberCalendar && loadMemberEvents(currentMemberCalendar.id, { quiet: true })}
      />
    );
  }

  if (step === 'create-calendar') {
    return (
      <CreateCalendarStep
        statusMessage={statusMessage}
        onOpenUserGuide={() => setShowUserGuide(true)}
        onCancel={() => {
          // (28 Oct 2026, decision 153) Back to the member page it was opened from.
          if (createReturnStep && calendars.length === 0) { setStep(createReturnStep); return; }
          setStep(calendars.length > 0 ? 'main' : 'welcome');
        }}
        isAuthenticated={isAuthenticated()}
        onSignOut={() => { clearAuthToken(); window.location.reload(); }}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        settingsInitialTab={settingsInitialTab}
        setSettingsInitialTab={setSettingsInitialTab}
        calendarName={calendarName}
        setCalendarName={setCalendarName}
        calendarDescription={calendarDescription}
        setCalendarDescription={setCalendarDescription}
        hostBoardEnabled={newCalendarHostBoard}
        setHostBoardEnabled={setNewCalendarHostBoard}
        onCreateCalendar={handleCreateCalendar}
        isCreatingCalendar={isCreatingCalendar}
        userEmail={userEmail}
        apiBaseUrl={API_BASE_URL}
        authToken={localStorage.getItem('auth_token')}
        onStatusMessage={showStatus}
        userTier={userTier}
        firstName={firstName}
        lastName={lastName}
        preferredName={preferredName}
        onSaveProfile={(fn, ln, pn) => { setFirstName(fn); setLastName(ln); setPreferredName(pn); }}
        isSA={isSA}
        raRegions={raRegions}
      />
    );
  }

  return (
    <DashboardStep
      statusMessage={statusMessage}
      currentCalendar={currentCalendar}
      calendars={calendars}
      userEmail={userEmail}
      preferredName={preferredName}
      firstName={firstName}
      lastName={lastName}
      isSA={isSA}
      raRegions={raRegions}
      authorityCalendars={authorityCalendars}
      canCreateCalendar={canCreateCalendar}
      userTier={userTier}
      showWeekday={showWeekday}
      events={events}
      sortedEvents={sortedEvents}
      showAddEvent={showAddEvent}
      showEditEvent={showEditEvent}
      editingEvent={editingEvent}
      newEvent={newEvent}
      isAddingEvent={isAddingEvent}
      eventUseCustomTz={eventUseCustomTz}
      eventDiffEndTz={eventDiffEndTz}
      eventStartTz={eventStartTz}
      eventEndTz={eventEndTz}
      calendarTimezone={calendarTimezone}
      whatsappNotifyModal={whatsappNotifyModal}
      tierError={tierError}
      showRenameCalendar={showRenameCalendar}
      renameCalendarInput={renameCalendarInput}
      showDeleteConfirm={showDeleteConfirm}
      showDeleteEventConfirm={showDeleteEventConfirm}
      eventToDelete={eventToDelete}
      pendingNotifications={pendingNotifications}
      showNotificationsPanel={showNotificationsPanel}
      resolvingNotificationId={resolvingNotificationId}
      showSettings={showSettings}
      settingsInitialTab={settingsInitialTab}
      showUserGuide={showUserGuide}
      userPreferredCurrency={userPreferredCurrency}
      step={step}
      setNewEvent={setNewEvent}
      setEventUseCustomTz={setEventUseCustomTz}
      setEventDiffEndTz={setEventDiffEndTz}
      setEventStartTz={setEventStartTz}
      setEventEndTz={setEventEndTz}
      setShowAddEvent={setShowAddEvent}
      setRenameCalendarInput={setRenameCalendarInput}
      setCalendarTimezone={setCalendarTimezone}
      setShowRenameCalendar={setShowRenameCalendar}
      setShowDeleteConfirm={setShowDeleteConfirm}
      setWhatsappNotifyModal={setWhatsappNotifyModal}
      setTierError={setTierError}
      setShowDeleteEventConfirm={setShowDeleteEventConfirm}
      setEventToDelete={setEventToDelete}
      setShowNotificationsPanel={setShowNotificationsPanel}
      setShowSettings={setShowSettings}
      setSettingsInitialTab={setSettingsInitialTab}
      setShowUserGuide={setShowUserGuide}
      setFirstName={setFirstName}
      setLastName={setLastName}
      setPreferredName={setPreferredName}
      handleCreateNewCalendar={handleCreateNewCalendar}
      handleSwitchCalendar={handleSwitchCalendar}
      memberCalendars={memberCalendars}
      onChooseCalendar={handleChooseCalendar}
      handleRenameCalendar={handleRenameCalendar}
      handleSaveCalendarTimezone={handleSaveCalendarTimezone}
      handleCalendarOptionsSaved={handleCalendarOptionsSaved}
      handleStartEventFromBoard={handleStartEventFromBoard}
      handleAnnounceHostBoard={handleAnnounceHostBoard}
      hostBoardVersion={hostBoardVersion}
      pendingHostSlotId={pendingHostSlotId}
      hostBoardTasks={hostBoardTasks}
      resolvingBoardTaskId={resolvingBoardTaskId}
      handleSendBoardTask={handleSendBoardTask}
      handleEmailBoardTask={handleEmailBoardTask}
      handleResolveBoardTask={resolveBoardTask}
      handleHostBoardChanged={fetchHostBoardTasks}
      handleDeleteCalendar={handleDeleteCalendar}
      addEvent={addEvent}
      saveEditEvent={saveEditEvent}
      cancelEdit={cancelEdit}
      startEditEvent={startEditEvent}
      confirmDeleteEvent={confirmDeleteEvent}
      deleteEvent={deleteEvent}
      shareToWhatsApp={shareToWhatsApp}
      handleSendWaNotification={handleSendWaNotification}
      handleSendPollNotification={handleSendPollNotification}
      resolveNotification={resolveNotification}
      isAuthenticated={isAuthenticated}
      clearAuthToken={clearAuthToken}
      showStatus={showStatus}
      editEventRef={editEventRef}
      API_BASE_URL={API_BASE_URL}
      onAnswerPollForMember={handleAnswerPollForMember}
      onRefreshEvents={refreshDashboardEvents}
      onRefreshNotifications={fetchPendingNotifications}
      onRefreshHostBoard={() => setHostBoardVersion(v => v + 1)}
      onViewPollResults={handleViewPollResultsFromDashboard}
      onAnswerPollAsSelf={handleAnswerPollAsSelfFromDashboard}
      scheduleChoice={scheduleChoice}
      onScheduleChoice={resolveScheduleChoice}
      onCancelScheduleChoice={() => { setScheduleChoice(null); editAnswersRef.current = {}; }}
      editQuestion={editQuestion}
      onEditQuestionAnswer={(answer) => { setEditQuestion(null); saveEditEvent(answer); }}
      onCancelEditQuestion={() => { setEditQuestion(null); editAnswersRef.current = {}; }}
      pollDatesQuestion={pollDatesQuestion}
      onOpenPollDates={handleOpenPollDates}
      onPollDatesAnswer={handlePollDatesAnswer}
      onCancelPollDates={() => setPollDatesQuestion(null)}
    />
  );
};

export default GoogleCalendarApp;
export { CURRENCY_SYMBOLS, CURRENCY_NAMES, formatPrice };
